import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabase';
import { uploadBudgetItemImage, removeBudgetItemImageFromStorage } from '../utils/budgetAttachments';
import { NotificationService } from '../utils/notificationService';
import { 
  notifyEntityCreated, 
  notifyEntityUpdated, 
  notifyEntityFinished 
} from '../utils/notifications';
import { getNextDeliveryNoteNumber } from '../utils/numberGenerator';
import { logActivity } from '../utils/activityLogger';
import { sortTaskProjects } from '../utils/projectUtils';
import {
  Client, 
  Supplier, 
  SupplierNote,
  Invoice, 
  InvoiceItem, 
  DeliveryNote,
  ServiceAgreement,
  Payment, 
  Production, 
  Sample, 
  SampleTask, 
  ProductionTask, 
  Express, 
  ExpressItem, 
  ExpressStock, 
  ExpressStockMovement, 
  ExpressSample, 
  ExpressSampleItem,
  TodoItem,
  ProductionTodo,
  ChatMessage,
  CrmLead,
  CrmReminder,
  Budget,
  BudgetItem,
  TaskBoard,
  TaskBoardTask,
  TaskResponsible,
  TaskCategory,
  TaskProject
} from '../types';

// Tipos locales para Aprobación de Fichas
export interface Ficha {
  id: string;
  title: string;
  pdf_url: string;
  status: 'in_review' | 'rejected' | 'approved';
  version: number;
  vendedor_id: string;
  vendedor_email?: string | null;
  kind?: 'produccion' | 'muestra';
  rejected_comment?: string | null;
  created_at: string;
  updated_at: string;
}

interface AppState {
  // UI state
  darkMode: boolean;
  toggleDarkMode: () => void;
  error: string | null;
  showError: boolean;
  setError: (error: string | null) => void;
  clearError: () => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;

  // Data
  clients: Client[];
  suppliers: Supplier[];
  supplierNotes: SupplierNote[];
  budgets: Budget[];
  invoices: Invoice[];
  deliveryNotes: DeliveryNote[];
  serviceAgreements: ServiceAgreement[];
  serviceAgreementTemplates: any[];
  payments: Payment[];
  productions: Production[];
  productionTasks: ProductionTask[];
  samples: Sample[];
  sampleTasks: SampleTask[];
  express: Express[];
  crmLeads: CrmLead[];
  crmReminders: CrmReminder[];
  expressItems: ExpressItem[];
  expressStock: ExpressStock[];
  expressStockMovements: ExpressStockMovement[];
  expressSamples: ExpressSample[];
  expressSampleItems: ExpressSampleItem[];
  todoItems: TodoItem[];
  productionTodos: ProductionTodo[];
  taskBoards: TaskBoard[];
  taskBoardTasks: TaskBoardTask[];
  taskResponsibles: TaskResponsible[];
  taskCategories: TaskCategory[];
  taskProjects: TaskProject[];
  fichas: Ficha[];
  fichaHistory: Record<string, any[]>; // cache por ficha
  fichasHistoryAll: any[];

  expenseCategories: any[];
  expenses: any[];
  currentUser: any | null;
  setCurrentUser: (user: any | null) => void;
  savedAddresses: any[];

  // Fetch functions
  fetchClients: () => Promise<void>;
  fetchSuppliers: () => Promise<void>;
  fetchInvoices: () => Promise<void>;
  fetchDeliveryNotes: () => Promise<void>;
  fetchServiceAgreements: () => Promise<void>;
  fetchPayments: () => Promise<void>;
  fetchProductions: () => Promise<void>;
  fetchProductionTasks: () => Promise<void>;
  fetchSamples: () => Promise<void>;
  fetchSampleTasks: () => Promise<void>;
  fetchExpress: () => Promise<void>;
  fetchExpressItems: () => Promise<void>;
  fetchExpressStock: () => Promise<void>;
  fetchExpressStockMovements: () => Promise<void>;
  fetchExpressSamples: () => Promise<void>;
  fetchExpressSampleItems: () => Promise<void>;
  fetchTodoItems: () => Promise<void>;
  fetchProductionTodos: (date?: string) => Promise<void>;
  fetchTaskBoards: () => Promise<void>;
  fetchTaskBoardTasks: (boardId: string) => Promise<void>;
  fetchTaskResponsibles: () => Promise<void>;
  fetchTaskCategories: () => Promise<void>;
  fetchTaskProjects: () => Promise<void>;
  fetchProjectTasks: (projectId: string) => Promise<void>;
  fetchAllProjectTasks: () => Promise<void>;
  fetchFichas: () => Promise<void>;
  fetchFichaHistory: (fichaId: string) => Promise<any[]>;
  fetchFichasHistoryAll: () => Promise<any[]>;

  fetchExpenseCategories: () => Promise<void>;
  fetchExpenses: () => Promise<void>;
  fetchSavedAddresses: () => Promise<void>;

  // CRUD functions
  // Clients
  addClient: (client: Omit<Client, 'id' | 'balance'>) => Promise<Client>;
  updateClient: (id: string, client: Omit<Client, 'id' | 'balance'>) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;

  // Suppliers
  addSupplier: (supplier: Omit<Supplier, 'id' | 'balance'>) => Promise<Supplier>;
  updateSupplier: (id: string, supplier: Omit<Supplier, 'id' | 'balance'>) => Promise<void>;
  deleteSupplier: (id: string) => Promise<void>;

  // Supplier Notes
  fetchSupplierNotes: (supplierId?: string) => Promise<void>;
  addSupplierNote: (supplierId: string, content: string) => Promise<SupplierNote>;
  updateSupplierNote: (id: string, content: string) => Promise<void>;
  deleteSupplierNote: (id: string) => Promise<void>;

  // Budgets
  fetchBudgets: () => Promise<void>;
  cleanupObsoleteBudgetTransactions: () => Promise<void>;
  addBudget: (
    budget: Omit<Budget, 'id' | 'items'>,
    items: (Omit<BudgetItem, 'id' | 'budget_id' | 'created_at'> & { pendingImage?: File | null })[]
  ) => Promise<Budget & { items: BudgetItem[] }>;
  updateBudget: (
    id: string,
    budget: Partial<Budget>,
    items?: (Omit<BudgetItem, 'budget_id' | 'created_at'> & {
      id?: string;
      pendingImage?: File | null;
      previewUrl?: string | null;
    })[]
  ) => Promise<Budget & { items: BudgetItem[] }>;
  deleteBudget: (id: string) => Promise<void>;
  convertBudgetToInvoice: (budgetId: string) => Promise<string>;

  // Invoices
  addInvoice: (invoice: Omit<Invoice, 'id'>, items: Omit<InvoiceItem, 'id' | 'invoice_id'>[]) => Promise<void>;
  updateInvoice: (id: string, invoice: Partial<Invoice>) => Promise<void>;
  deleteInvoice: (id: string) => Promise<void>;
  voidInvoice: (id: string) => Promise<void>;

  // Delivery Notes
  addDeliveryNote: (deliveryNote: Omit<DeliveryNote, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateDeliveryNote: (id: string, deliveryNote: Partial<DeliveryNote>) => Promise<void>;
  deleteDeliveryNote: (id: string) => Promise<void>;

  // Service Agreements
  addServiceAgreement: (agreement: Omit<ServiceAgreement, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateServiceAgreement: (id: string, agreement: Partial<ServiceAgreement>) => Promise<void>;
  deleteServiceAgreement: (id: string) => Promise<void>;

  // Payments
  addPayment: (payment: Omit<Payment, 'id'>) => Promise<void>;
  updatePayment: (id: string, payment: Partial<Payment>) => Promise<void>;
  deletePayment: (id: string) => Promise<void>;
  voidPayment: (id: string) => Promise<void>;

  // Productions
  addProduction: (production: Omit<Production, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateProduction: (id: string, production: Partial<Production>) => Promise<void>;
  deleteProduction: (id: string) => Promise<void>;

  // Production Tasks
  addProductionTask: (task: Omit<ProductionTask, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateProductionTask: (id: string, task: Partial<ProductionTask>) => Promise<void>;
  deleteProductionTask: (id: string) => Promise<void>;

  // Samples
  addSample: (sample: Omit<Sample, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateSample: (id: string, sample: Partial<Sample>) => Promise<void>;
  deleteSample: (id: string) => Promise<void>;

  // Archived helpers
  fetchArchivedProductions: () => Promise<void>;
  fetchArchivedSamples: () => Promise<void>;
  restoreProduction: (id: string) => Promise<void>;
  restoreSample: (id: string) => Promise<void>;
  getArchivedProductions: () => Promise<Production[]>;
  getArchivedSamples: () => Promise<Sample[]>;
  hardDeleteProduction: (id: string) => Promise<void>;
  hardDeleteSample: (id: string) => Promise<void>;

  // Fichas
  addFicha: (payload: { title: string; pdf_url: string; vendedor_id: string; kind?: 'produccion'|'muestra' }) => Promise<Ficha>;
  updateFichaStatus: (id: string, status: Ficha['status'], comment?: string, imageUrls?: string[]) => Promise<void>;
  deleteFicha: (id: string) => Promise<void>;
  replaceFichaFile: (id: string, newUrl: string, newTitle?: string) => Promise<void>;

  // CRM functions
  fetchCrmLeads: () => Promise<void>;
  addCrmLead: (lead: Omit<CrmLead, 'id' | 'created_at' | 'updated_at'>) => Promise<CrmLead>;
  updateCrmLead: (id: string, updates: Partial<CrmLead>) => Promise<CrmLead>;
  deleteCrmLead: (id: string) => Promise<void>;
  fetchCrmReminders: () => Promise<void>;
  addCrmReminder: (reminder: Omit<CrmReminder, 'id' | 'created_at'>) => Promise<CrmReminder>;
  updateCrmReminder: (id: string, updates: Partial<CrmReminder>) => Promise<CrmReminder>;
  deleteCrmReminder: (id: string) => Promise<void>;

  // Sample Tasks
  addSampleTask: (task: Omit<SampleTask, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateSampleTask: (id: string, task: Partial<SampleTask>) => Promise<void>;
  deleteSampleTask: (id: string) => Promise<void>;

  // Express
  addExpress: (express: Omit<Express, 'id' | 'created_at' | 'updated_at'>, items: Omit<ExpressItem, 'id' | 'express_id' | 'created_at'>[]) => Promise<string>;
  updateExpress: (id: string, express: Partial<Express> & { items?: Omit<ExpressItem, 'id' | 'express_id' | 'created_at'>[] }) => Promise<void>;
  deleteExpress: (id: string) => Promise<void>;

  // Express Stock
  addExpressStock: (stock: Omit<ExpressStock, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateExpressStock: (id: string, stock: Partial<ExpressStock>) => Promise<void>;
  deleteExpressStock: (id: string) => Promise<void>;

  // Express Stock Movements
  addExpressStockMovement: (movement: Omit<ExpressStockMovement, 'id' | 'created_at'>) => Promise<void>;

  // Express Samples
  addExpressSample: (sample: Omit<ExpressSample, 'id' | 'created_at' | 'updated_at'>, items: Omit<ExpressSampleItem, 'id' | 'express_sample_id' | 'created_at'>[]) => Promise<string>;
  updateExpressSample: (id: string, sample: Partial<ExpressSample> & { items?: Omit<ExpressSampleItem, 'id' | 'express_sample_id' | 'created_at'>[] }) => Promise<void>;
  deleteExpressSample: (id: string) => Promise<void>;

  // Todo Items
  addTodoItem: (item: Omit<TodoItem, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateTodoItem: (id: string, item: Partial<TodoItem>) => Promise<void>;
  deleteTodoItem: (id: string) => Promise<void>;
  toggleTodoItemCompletion: (id: string, completed: boolean) => Promise<void>;

  // Production Todos
  addProductionTodo: (item: Omit<ProductionTodo, 'id' | 'created_at' | 'updated_at' | 'user_id' | 'supplier_name'>) => Promise<void>;
  updateProductionTodo: (id: string, item: Partial<ProductionTodo>) => Promise<void>;
  deleteProductionTodo: (id: string) => Promise<void>;
  toggleProductionTodoCompletion: (id: string, completed: boolean) => Promise<void>;

  // Task Boards
  addTaskBoard: (board: Omit<TaskBoard, 'id' | 'created_at' | 'updated_at' | 'is_archived'>) => Promise<TaskBoard>;
  updateTaskBoard: (id: string, updates: Partial<TaskBoard>) => Promise<TaskBoard>;
  deleteTaskBoard: (id: string) => Promise<void>;

  addTaskBoardTask: (task: Omit<TaskBoardTask, 'id' | 'created_at' | 'updated_at'>) => Promise<TaskBoardTask>;
  updateTaskBoardTask: (id: string, updates: Partial<TaskBoardTask>) => Promise<TaskBoardTask>;
  deleteTaskBoardTask: (id: string) => Promise<void>;

  addTaskResponsible: (responsible: Omit<TaskResponsible, 'id' | 'created_at' | 'updated_at' | 'is_active'>) => Promise<TaskResponsible>;
  updateTaskResponsible: (id: string, updates: Partial<TaskResponsible>) => Promise<TaskResponsible>;
  deleteTaskResponsible: (id: string) => Promise<void>;

  addTaskCategory: (category: Omit<TaskCategory, 'id' | 'created_at' | 'updated_at' | 'is_active'>) => Promise<TaskCategory>;
  updateTaskCategory: (id: string, updates: Partial<TaskCategory>) => Promise<TaskCategory>;
  deleteTaskCategory: (id: string) => Promise<void>;

  addTaskProject: (project: Omit<TaskProject, 'id' | 'created_at' | 'updated_at' | 'is_active'>) => Promise<TaskProject>;
  updateTaskProject: (id: string, updates: Partial<TaskProject>) => Promise<TaskProject>;
  reorderTaskProjects: (orderedIds: string[]) => Promise<void>;
  deleteTaskProject: (id: string) => Promise<void>;

  // Chat Messages
  addChatMessage: (message: Omit<ChatMessage, 'id' | 'created_at'>) => Promise<void>;
  deleteChatMessage: (id: string) => Promise<void>;
  toggleChatMessageReaction: (messageId: string, userId: string) => Promise<void>;

  // Expenses
  addExpense: (expense: Omit<any, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateExpense: (id: string, expense: Partial<any>) => Promise<void>;
  deleteExpense: (id: string) => Promise<void>;

  // Saved Addresses
  addSavedAddress: (address: Omit<any, 'id' | 'created_at' | 'updated_at'>) => Promise<any>;
  updateSavedAddress: (id: string, address: Partial<any>) => Promise<void>;
  deleteSavedAddress: (id: string) => Promise<void>;

  // Salespersons
  salespersons: { id: string; name: string; email: string }[];
  fetchSalespersons: () => Promise<void>;

  // Utility functions
  calculateEntityBalance: (entityId: string, entityType: 'client' | 'supplier') => number;
  /** Mismo cálculo que la sección Clientes: saldo por moneda (ARS / USD) sin convertir */
  getEntityBalanceByCurrency: (entityId: string, entityType: 'client' | 'supplier') => { ARS: number; USD: number };
  getEntityBillingByCurrency: (entityId: string, entityType: 'client' | 'supplier') => { ARS: number; USD: number };
}

export const useAppStore = create<AppState>((set, get) => ({
  // UI state
  darkMode: localStorage.getItem('darkMode') === 'true',
  toggleDarkMode: () => {
    const newDarkMode = !get().darkMode;
    localStorage.setItem('darkMode', newDarkMode.toString());
    set({ darkMode: newDarkMode });
  },
  error: null,
  showError: false,
  setError: (error) => set({ error, showError: !!error }),
  clearError: () => set({ error: null, showError: false }),
  isLoading: false,
  setIsLoading: (isLoading) => set({ isLoading }),

  // Data
  clients: [],
  suppliers: [],
  supplierNotes: [],
  budgets: [],
  invoices: [],
  deliveryNotes: [],
  serviceAgreements: [],
  serviceAgreementTemplates: [],
  payments: [],
  productions: [],
  productionTasks: [],
  samples: [],
  sampleTasks: [],
  express: [],
  expressItems: [],
  expressStock: [],
  expressStockMovements: [],
  expressSamples: [],
  expressSampleItems: [],
  todoItems: [],
  productionTodos: [],
  taskBoards: [],
  taskBoardTasks: [],
  taskResponsibles: [],
  taskCategories: [],
  taskProjects: [],
  fichas: [],
  fichaHistory: {},
  fichasHistoryAll: [],
  crmLeads: [],
  crmReminders: [],

  expenseCategories: [],
  expenses: [],
  currentUser: null,
  setCurrentUser: (user) => set({ currentUser: user }),
  savedAddresses: [],
  salespersons: [],

  // Fetch functions
  fetchClients: async () => {
    try {
      const { data, error } = await supabase.from('clients').select('*').order('name');
      if (error) throw error;
      set({ clients: data || [] });
    } catch (error: any) {
      console.error('Error fetching clients:', error);
      get().setError(error.message);
    }
  },

  fetchSuppliers: async () => {
    try {
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .order('name');
      
      if (error) throw error;
      set({ suppliers: data || [] });
    } catch (error: any) {
      console.error('Error fetching suppliers:', error);
      get().setError(error.message);
    }
  },

  fetchInvoices: async () => {
    try {
      console.log('Fetching invoices from Supabase...');
      
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .order('date', { ascending: false });
      
      if (error) {
        console.error('Supabase error fetching invoices:', error);
        throw new Error(`Database error: ${error.message}`);
      }
      
      console.log(`Successfully fetched ${data?.length || 0} invoices`);
      set({ invoices: data || [] });
    } catch (error: any) {
      console.error('Error in fetchInvoices:', error);
      
      // Import NetworkErrorHandler dynamically to avoid circular dependencies
      const { NetworkErrorHandler } = await import('../utils/networkErrorHandler');
      
      // Handle network errors safely without causing logout
      if (NetworkErrorHandler.isNetworkError(error)) {
        console.warn('Network error in fetchInvoices, keeping user logged in');
        get().setError('Error de conexión: No se pudo conectar con la base de datos. Verifique su conexión a internet.');
        return; // Don't throw, just show error and continue
      }
      
      // Re-throw other errors
      throw error;
    }
  },

  fetchDeliveryNotes: async () => {
    try {
      console.log('Fetching delivery notes from Supabase...');
      
      const { data, error } = await supabase
        .from('delivery_notes')
        .select('*')
        .order('date', { ascending: false });
      
      if (error) {
        console.error('Supabase error fetching delivery notes:', error);
        throw error;
      }
      
      console.log(`Successfully fetched ${data?.length || 0} delivery notes`);
      set({ deliveryNotes: data || [] });
    } catch (error: any) {
      console.error('Error in fetchDeliveryNotes:', error);
      
      // Import NetworkErrorHandler dynamically to avoid circular dependencies
      const { NetworkErrorHandler } = await import('../utils/networkErrorHandler');
      
      // Handle network errors safely without causing logout
      if (NetworkErrorHandler.isNetworkError(error)) {
        console.warn('Network error in fetchDeliveryNotes, keeping user logged in');
        get().setError('Error de conexión: No se pudo conectar con la base de datos. Verifique su conexión a internet.');
        return; // Don't throw, just show error and continue
      }
      
      // Re-throw other errors
      throw error;
    }
  },

  fetchServiceAgreements: async () => {
    try {
      console.log('Fetching service agreements from Supabase...');
      
      const { data, error } = await supabase
        .from('service_agreements')
        .select('*')
        .order('contract_date', { ascending: false });
      
      if (error) {
        console.error('Supabase error fetching service agreements:', error);
        throw error;
      }
      
      console.log(`Successfully fetched ${data?.length || 0} service agreements`);
      set({ serviceAgreements: data || [] });
    } catch (error: any) {
      console.error('Error in fetchServiceAgreements:', error);
      
      // Import NetworkErrorHandler dynamically to avoid circular dependencies
      const { NetworkErrorHandler } = await import('../utils/networkErrorHandler');
      
      // Handle network errors safely without causing logout
      if (NetworkErrorHandler.isNetworkError(error)) {
        console.warn('Network error in fetchServiceAgreements, keeping user logged in');
        get().setError('Error de conexión: No se pudo conectar con la base de datos. Verifique su conexión a internet.');
        return; // Don't throw, just show error and continue
      }
      
      // Re-throw other errors
      throw error;
    }
  },

  fetchPayments: async () => {
    try {
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .order('date', { ascending: false });
      
      if (error) throw error;
      set({ payments: data || [] });
    } catch (error: any) {
      console.error('Error fetching payments:', error);
      get().setError(error.message);
    }
  },

  fetchProductions: async () => {
    try {
      const { data, error } = await supabase
        .from('productions')
        .select('*')
        .eq('archived', false)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      set({ productions: data || [] });
    } catch (error: any) {
      console.error('Error fetching productions:', error);
      get().setError(error.message);
    }
  },

  fetchProductionTasks: async () => {
    try {
      const { data, error } = await supabase
        .from('production_tasks')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      set({ productionTasks: data || [] });
    } catch (error: any) {
      console.error('Error fetching production tasks:', error);
      get().setError(error.message);
    }
  },

  fetchSamples: async () => {
    try {
      const { data, error } = await supabase
        .from('samples')
        .select('*')
        .eq('archived', false)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      set({ samples: data || [] });
    } catch (error: any) {
      console.error('Error fetching samples:', error);
      get().setError(error.message);
    }
  },

  fetchArchivedProductions: async () => {
    try {
      const { data, error } = await supabase
        .from('productions')
        .select('*')
        .eq('archived', true)
        .order('archived_at', { ascending: false });
      if (error) throw error;
      set({ productions: data || [] });
    } catch (error: any) {
      console.error('Error fetching archived productions:', error);
      get().setError(error.message);
    }
  },

  fetchArchivedSamples: async () => {
    try {
      const { data, error } = await supabase
        .from('samples')
        .select('*')
        .eq('archived', true)
        .order('archived_at', { ascending: false });
    
      if (error) throw error;
      set({ samples: data || [] });
    } catch (error: any) {
      console.error('Error fetching archived samples:', error);
      get().setError(error.message);
    }
  },

  getArchivedProductions: async () => {
    const { data, error } = await supabase
      .from('productions')
      .select('*')
      .eq('archived', true)
      .order('archived_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  getArchivedSamples: async () => {
    const { data, error } = await supabase
      .from('samples')
      .select('*')
      .eq('archived', true)
      .order('archived_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  hardDeleteProduction: async (id) => {
    try {
      const { error } = await supabase
        .from('productions')
        .delete()
        .eq('id', id);
      if (error) throw error;
    } catch (error: any) {
      console.error('Error hard deleting production:', error);
      get().setError(error.message);
      throw error;
    }
  },

  hardDeleteSample: async (id) => {
    try {
      const { error } = await supabase
        .from('samples')
        .delete()
        .eq('id', id);
      if (error) throw error;
    } catch (error: any) {
      console.error('Error hard deleting sample:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Fichas
  fetchFichas: async () => {
    try {
      const { data, error } = await supabase
        .from('fichas_aprobacion')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      set({ fichas: (data || []) as unknown as Ficha[] });
    } catch (error: any) {
      console.error('Error fetching fichas:', error);
      get().setError(error.message);
    }
  },

  fetchFichaHistory: async (fichaId) => {
    try {
      const { data, error } = await supabase
        .from('fichas_aprobacion_history')
        .select('*')
        .eq('ficha_id', fichaId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      set(state => ({ fichaHistory: { ...state.fichaHistory, [fichaId]: data || [] } }));
      return data || [];
    } catch (error: any) {
      console.error('Error fetching ficha history:', error);
      get().setError(error.message);
      return [];
    }
  },

  fetchFichasHistoryAll: async () => {
    try {
      const { data, error } = await supabase
        .from('fichas_aprobacion_history')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      set({ fichasHistoryAll: data || [] });
      return data || [];
    } catch (error: any) {
      console.error('Error fetching fichas history all:', error);
      get().setError(error.message);
      return [];
    }
  },

  addFicha: async ({ title, pdf_url, vendedor_id, kind }) => {
    const payload = { title, pdf_url, vendedor_id, vendedor_email: get().currentUser?.email || null, kind: kind || 'produccion', status: 'in_review' as const };
    const { data, error } = await supabase
      .from('fichas_aprobacion')
      .insert([payload])
      .select()
      .single();
    if (error) throw error;
    set(state => ({ fichas: [data as unknown as Ficha, ...state.fichas] }));
    // Insert history row
    await supabase.from('fichas_aprobacion_history').insert([
      { ficha_id: (data as any).id, status: 'in_review', comment: 'Carga inicial', changed_by: vendedor_id }
    ]);
    try {
      await NotificationService.notifyFichaSubmitted((data as any).id, title, get().currentUser?.email);
    } catch {}
    return data as unknown as Ficha;
  },

  updateFichaStatus: async (id, status, comment, imageUrls) => {
    try {
      // Verificar si ya existe un registro reciente con el mismo status para evitar duplicados
      const { data: recentHistory } = await supabase
        .from('fichas_aprobacion_history')
        .select('id, created_at')
        .eq('ficha_id', id)
        .eq('status', status)
        .eq('changed_by', get().currentUser?.id)
        .gte('created_at', new Date(Date.now() - 5000).toISOString()) // Últimos 5 segundos
        .order('created_at', { ascending: false })
        .limit(1);

      // Si ya existe un registro reciente, no hacer nada
      if (recentHistory && recentHistory.length > 0) {
        console.log('Ya existe un registro reciente para este cambio, evitando duplicado');
        return;
      }

      // Si se desaprueba, la ficha vuelve a 'in_review' pero con comentario de rechazo
      const updates: any = { status: status === 'rejected' ? 'in_review' : status };
      if (status === 'rejected') updates.rejected_comment = comment || '';
      
      const { error } = await supabase
        .from('fichas_aprobacion')
        .update(updates)
        .eq('id', id);
      if (error) throw error;
      
      await supabase.from('fichas_aprobacion_history').insert([
        { ficha_id: id, status, comment: comment || '', image_urls: imageUrls || [], changed_by: get().currentUser?.id }
      ]);
      
      try {
        const ficha = (get().fichas || []).find(f => f.id === id);
        if (ficha) {
          if (status === 'approved') await NotificationService.notifyFichaApproved(ficha.vendedor_id, id, ficha.title);
          if (status === 'rejected') await NotificationService.notifyFichaRejected(ficha.vendedor_id, id, ficha.title, comment || '');
        }
      } catch {}
      
      await get().fetchFichas();
    } catch (error: any) {
      console.error('Error updating ficha status:', error);
      throw error;
    }
  },

  deleteFicha: async (id) => {
    const { error } = await supabase.from('fichas_aprobacion').delete().eq('id', id);
    if (error) throw error;
    set(state => ({ fichas: state.fichas.filter(f => f.id !== id) }));
  },

  replaceFichaFile: async (id, newUrl, newTitle) => {
    const { error } = await supabase
      .from('fichas_aprobacion')
      .update({ pdf_url: newUrl, title: newTitle || undefined, status: 'in_review', rejected_comment: null })
      .eq('id', id);
    if (error) throw error;
    await supabase.from('fichas_aprobacion_history').insert([
      { ficha_id: id, status: 'in_review', comment: 'Reemplazo de archivo', changed_by: get().currentUser?.id, image_urls: '{}' }
    ]);
    try {
      const ficha = (get().fichas || []).find(f => f.id === id);
      if (ficha) {
        await NotificationService.notifyFichaReplaced(id, ficha.title, ficha.vendedor_email || undefined);
      }
    } catch {}
    await get().fetchFichas();
  },

  fetchSampleTasks: async () => {
    try {
      const { data, error } = await supabase
        .from('sample_tasks')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      set({ sampleTasks: data || [] });
    } catch (error: any) {
      console.error('Error fetching sample tasks:', error);
      get().setError(error.message);
    }
  },

  fetchExpress: async () => {
    try {
      const { data, error } = await supabase
        .from('express')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      set({ express: data || [] });
    } catch (error: any) {
      console.error('Error fetching express orders:', error);
      // Provide more specific error messages for network issues
      const errorMessage = error.message?.includes('fetch') || error.name === 'TypeError'
        ? 'Error de conexión al cargar órdenes express. Verifique su conexión a internet.'
        : error.message;
      get().setError(errorMessage);
      throw error; // Re-throw to allow caller to handle
    }
  },

  fetchExpressItems: async () => {
    try {
      const { data, error } = await supabase
        .from('express_items')
        .select('*');
      
      if (error) throw error;
      set({ expressItems: data || [] });
    } catch (error: any) {
      console.error('Error fetching express items:', error);
      get().setError(error.message);
    }
  },

  fetchExpressStock: async () => {
    try {
      const { data, error } = await supabase
        .from('express_stock')
        .select('*')
        .order('product_type, color, size');
      
      if (error) throw error;
      set({ expressStock: data || [] });
    } catch (error: any) {
      console.error('Error fetching express stock:', error);
      get().setError(error.message);
    }
  },

  fetchExpressStockMovements: async () => {
    try {
      const { data, error } = await supabase
        .from('express_stock_movements')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      set({ expressStockMovements: data || [] });
    } catch (error: any) {
      console.error('Error fetching express stock movements:', error);
      // Provide more specific error messages for network issues
      const errorMessage = error.message?.includes('fetch') || error.name === 'TypeError'
        ? 'Error de conexión al cargar movimientos de stock express. Verifique su conexión a internet.'
        : error.message;
      get().setError(errorMessage);
      throw error; // Re-throw to allow caller to handle
    }
  },

  fetchExpressSamples: async () => {
    try {
      const { data, error } = await supabase
        .from('express_samples')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      set({ expressSamples: data || [] });
    } catch (error: any) {
      console.error('Error fetching express samples:', error);
      get().setError(error.message);
    }
  },

  fetchExpressSampleItems: async () => {
    try {
      const { data, error } = await supabase
        .from('express_sample_items')
        .select('*');
      
      if (error) throw error;
      set({ expressSampleItems: data || [] });
    } catch (error: any) {
      console.error('Error fetching express sample items:', error);
      get().setError(error.message);
    }
  },

  fetchTodoItems: async () => {
    try {
      const { data, error } = await supabase
        .from('todo_items')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      set({ todoItems: data || [] });
    } catch (error: any) {
      console.error('Error fetching todo items:', error);
      get().setError(error.message);
    }
  },

  // Task boards - gestión
  fetchTaskBoards: async () => {
    try {
      const { data, error } = await supabase
        .from('task_boards')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      set({ taskBoards: data || [] });
    } catch (error: any) {
      console.error('Error fetching task boards:', error);
      get().setError(error.message);
    }
  },

  fetchTaskBoardTasks: async (boardId: string) => {
    try {
      const { data, error } = await supabase
        .from('task_board_tasks')
        .select('*')
        .eq('board_id', boardId)
        .order('position', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;

      set(state => ({
        taskBoardTasks: [
          // mantener tareas de otros tableros en memoria
          ...state.taskBoardTasks.filter(t => t.board_id !== boardId),
          ...(data || []),
        ],
      }));
    } catch (error: any) {
      console.error('Error fetching task board tasks:', error);
      get().setError(error.message);
    }
  },

  fetchTaskResponsibles: async () => {
    try {
      const { data, error } = await supabase
        .from('task_responsibles')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      set({ taskResponsibles: data || [] });
    } catch (error: any) {
      console.error('Error fetching task responsibles:', error);
      get().setError(error.message);
    }
  },

  fetchTaskCategories: async () => {
    try {
      const { data, error } = await supabase
        .from('task_categories')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      set({ taskCategories: data || [] });
    } catch (error: any) {
      console.error('Error fetching task categories:', error);
      get().setError(error.message);
    }
  },

  fetchTaskProjects: async () => {
    try {
      const { data, error } = await supabase
        .from('task_projects')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;
      set({ taskProjects: sortTaskProjects(data || []) });
    } catch (error: any) {
      console.error('Error fetching task projects:', error);
      get().setError(error.message);
    }
  },

  fetchProjectTasks: async (projectId: string) => {
    try {
      const { data, error } = await supabase
        .from('task_board_tasks')
        .select('*')
        .eq('project_id', projectId)
        .order('position', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;

      set(state => ({
        taskBoardTasks: [
          ...state.taskBoardTasks.filter(t => t.project_id !== projectId),
          ...(data || []),
        ],
      }));
    } catch (error: any) {
      console.error('Error fetching project tasks:', error);
      get().setError(error.message);
    }
  },

  fetchAllProjectTasks: async () => {
    try {
      const { data, error } = await supabase
        .from('task_board_tasks')
        .select('*')
        .not('project_id', 'is', null)
        .order('position', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;
      set({ taskBoardTasks: data || [] });
    } catch (error: any) {
      console.error('Error fetching all project tasks:', error);
      get().setError(error.message);
    }
  },

  fetchProductionTodos: async (date?: string) => {
    try {
      console.log('fetchProductionTodos called with date:', date);
      
      let query = supabase
        .from('production_todos')
        .select(`
          *,
          suppliers:supplier_id (
            name
          )
        `)
        .order('created_at', { ascending: false });

      if (date) {
        query = query.eq('date', date);
      }

      const { data, error } = await query;
      
      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Fetched data:', data);
      
      // Transform data to include supplier_name
      const transformedData = (data || []).map(item => ({
        ...item,
        supplier_name: item.suppliers?.name || null
      }));
      
      console.log('Transformed data:', transformedData);
      
      if (date) {
        // If fetching for a specific date, update only that date's tasks
        set(state => {
          const otherDatesTasks = state.productionTodos.filter(task => task.date !== date);
          return { productionTodos: [...otherDatesTasks, ...transformedData] };
        });
      } else {
        // If fetching all tasks, replace the entire list
        set({ productionTodos: transformedData });
      }
      
      console.log('Store updated successfully');
    } catch (error: any) {
      console.error('Error fetching production todos:', error);
      get().setError(error.message);
    }
  },

  fetchExpenseCategories: async () => {
    try {
      const { data, error } = await supabase
        .from('expenses_categories')
        .select('*')
        .order('name');
      
      if (error) throw error;
      set({ expenseCategories: data || [] });
    } catch (error: any) {
      console.error('Error fetching expense categories:', error);
      get().setError(error.message);
    }
  },

  fetchExpenses: async () => {
    try {
      const { data, error } = await supabase
        .from('expenses')
        .select('*')
        .order('date', { ascending: false });
      
      if (error) throw error;
      set({ expenses: data || [] });
    } catch (error: any) {
      console.error('Error fetching expenses:', error);
      get().setError(error.message);
    }
  },

  fetchSavedAddresses: async () => {
    try {
      const { data, error } = await supabase.from('saved_addresses').select('*').order('name');
      if (error) throw error;
      set({ savedAddresses: data || [] });
    } catch (error: any) {
      console.error('Error fetching saved addresses:', error);
      get().setError(error.message);
    }
  },

  fetchSalespersons: async () => {
    try {
      const { data, error } = await supabase
        .from('safe_users_with_roles')
        .select('id, email, role')
        .eq('role', 'Vendedor')
        .order('email');
      
      if (error) throw error;
      
      const salespersons = (data || []).map(user => ({
        id: user.id,
        name: user.email.split('@')[0], // Use email prefix as name
        email: user.email
      }));
      
      set({ salespersons });
    } catch (error) {
      console.error('Error fetching salespersons:', error);
      // Fallback to hardcoded values if database fails
      set({ 
        salespersons: [
          { id: 'mateo', name: 'Mateo', email: 'mateo@productex.com.ar' },
          { id: 'male', name: 'Male', email: 'malena@productex.com.ar' },
          { id: 'joaquin', name: 'Joaquín', email: 'joaquin@productex.com.ar' }
        ]
      });
    }
  },

  // CRUD functions
  // Clients
  addClient: async (client) => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .insert([client])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        clients: [...state.clients, data]
      }));
      
      logActivity({ action: 'Cliente creado', entity_type: 'client', entity_id: data.id, details: data.name, action_type: 'create' });
      return data;
    } catch (error: any) {
      console.error('Error adding client:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateClient: async (id, client) => {
    try {
      const { error } = await supabase
        .from('clients')
        .update(client)
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        clients: state.clients.map(c => c.id === id ? { ...c, ...client } : c)
      }));
      logActivity({ action: 'Cliente actualizado', entity_type: 'client', entity_id: id, details: (client as any).name || id, action_type: 'update' });
    } catch (error: any) {
      console.error('Error updating client:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteClient: async (id) => {
    try {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      const deletedName = get().clients.find(c => c.id === id)?.name || id;
      set(state => ({
        clients: state.clients.filter(c => c.id !== id)
      }));
      logActivity({ action: 'Cliente eliminado', entity_type: 'client', entity_id: id, details: deletedName, action_type: 'delete' });
    } catch (error: any) {
      console.error('Error deleting client:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Suppliers
  addSupplier: async (supplier) => {
    try {
      const { data, error } = await supabase
        .from('suppliers')
        .insert([supplier])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        suppliers: [...state.suppliers, data]
      }));
      
      logActivity({ action: 'Proveedor creado', entity_type: 'supplier', entity_id: data.id, details: data.name, action_type: 'create' });
      return data;
    } catch (error: any) {
      console.error('Error adding supplier:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateSupplier: async (id, supplier) => {
    try {
      const { error } = await supabase
        .from('suppliers')
        .update(supplier)
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        suppliers: state.suppliers.map(s => s.id === id ? { ...s, ...supplier } : s)
      }));
      logActivity({ action: 'Proveedor actualizado', entity_type: 'supplier', entity_id: id, details: (supplier as any).name || id, action_type: 'update' });
    } catch (error: any) {
      console.error('Error updating supplier:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteSupplier: async (id) => {
    try {
      const { error } = await supabase
        .from('suppliers')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      const deletedName = get().suppliers.find(s => s.id === id)?.name || id;
      set(state => ({
        suppliers: state.suppliers.filter(s => s.id !== id)
      }));
      logActivity({ action: 'Proveedor eliminado', entity_type: 'supplier', entity_id: id, details: deletedName, action_type: 'delete' });
    } catch (error: any) {
      console.error('Error deleting supplier:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Supplier Notes functions
  fetchSupplierNotes: async (supplierId) => {
    try {
      let query = supabase
        .from('supplier_notes')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (supplierId) {
        query = query.eq('supplier_id', supplierId);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      set({ supplierNotes: data || [] });
    } catch (error: any) {
      console.error('Error fetching supplier notes:', error);
      get().setError(error.message);
    }
  },

  addSupplierNote: async (supplierId, content) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('supplier_notes')
        .insert({
          supplier_id: supplierId,
          content: content.trim(),
          created_by: user.id
        })
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        supplierNotes: [data, ...state.supplierNotes]
      }));
      
      return data;
    } catch (error: any) {
      console.error('Error adding supplier note:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateSupplierNote: async (id, content) => {
    try {
      const { data, error } = await supabase
        .from('supplier_notes')
        .update({
          content: content.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        supplierNotes: state.supplierNotes.map(note => 
          note.id === id ? data : note
        )
      }));
    } catch (error: any) {
      console.error('Error updating supplier note:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteSupplierNote: async (id) => {
    try {
      const { error } = await supabase
        .from('supplier_notes')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        supplierNotes: state.supplierNotes.filter(note => note.id !== id)
      }));
    } catch (error: any) {
      console.error('Error deleting supplier note:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Budgets
  fetchBudgets: async () => {
    try {
      console.log('Fetching budgets...');
      const { data, error } = await supabase
        .from('budgets')
        .select(`
          *,
          items:budget_items(*)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching budgets:', error);
        throw error;
      }
      
      console.log('Budgets fetched successfully:', data?.length || 0, 'budgets');
      set({ budgets: data || [] });
    } catch (error: any) {
      console.error('Error fetching budgets:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Función para limpiar transacciones obsoletas de presupuestos
  cleanupObsoleteBudgetTransactions: async () => {
    try {
      console.log('Cleaning up obsolete budget transactions...');
      
      // Obtener todos los presupuestos actuales
      const { data: currentBudgets, error: fetchError } = await supabase
        .from('budgets')
        .select('id');
      
      if (fetchError) {
        console.error('Error fetching current budgets:', fetchError);
        return;
      }
      
      const currentBudgetIds = new Set(currentBudgets?.map(b => b.id) || []);
      console.log('Current budget IDs:', currentBudgetIds.size);
      
      // Obtener todos los presupuestos en el estado local
      const localBudgets = get().budgets;
      const localBudgetIds = new Set(localBudgets.map(b => b.id));
      
      // Encontrar presupuestos que están en el estado local pero no en la base de datos
      const obsoleteBudgetIds = [...localBudgetIds].filter(id => !currentBudgetIds.has(id));
      
      if (obsoleteBudgetIds.length > 0) {
        console.log('Found obsolete budgets:', obsoleteBudgetIds);
        
        // Remover presupuestos obsoletos del estado local
        set(state => ({
          budgets: state.budgets.filter(b => !obsoleteBudgetIds.includes(b.id))
        }));
        
        console.log('Cleaned up obsolete budgets from local state');
      } else {
        console.log('No obsolete budgets found');
      }
      
    } catch (error: any) {
      console.error('Error cleaning up obsolete budget transactions:', error);
    }
  },

  addBudget: async (budget, items) => {
    try {
      console.log('Starting budget creation:', { budget, items });
      
      // Generar número de presupuesto
      console.log('Generating budget number...');
      const { data: numberData, error: numberError } = await supabase
        .rpc('get_next_budget_number');
      
      if (numberError) {
        console.error('Error generating budget number:', numberError);
        throw new Error(`Error generando número de presupuesto: ${numberError.message}`);
      }
      
      console.log('Budget number generated:', numberData);
      
      const budgetWithNumber = {
        ...budget,
        number: numberData
      };

      console.log('Creating budget with data:', budgetWithNumber);
      
      // Crear presupuesto
      const { data: budgetData, error: budgetError } = await supabase
        .from('budgets')
        .insert(budgetWithNumber)
        .select()
        .single();

      if (budgetError) {
        console.error('Error creating budget:', budgetError);
        throw new Error(`Error creando presupuesto: ${budgetError.message}`);
      }

      console.log('Budget created successfully:', budgetData);

      // Crear items del presupuesto
      const insertedItems: BudgetItem[] = [];
      if (items.length > 0) {
        console.log('Creating budget items:', items);

        for (let i = 0; i < items.length; i++) {
          const { pendingImage, previewUrl: _previewUrl, ...itemRest } = items[i] as typeof items[number] & {
            previewUrl?: string | null;
          };
          let image_url = itemRest.image_url ?? null;

          if (pendingImage) {
            image_url = await uploadBudgetItemImage(budgetData.id, pendingImage, i);
          }

          const { data: inserted, error: itemError } = await supabase
            .from('budget_items')
            .insert([{
              ...itemRest,
              image_url,
              budget_id: budgetData.id,
            }])
            .select()
            .single();

          if (itemError) {
            console.error('Error creating budget item:', itemError);
            await supabase.from('budgets').delete().eq('id', budgetData.id);
            throw new Error(`Error creando items del presupuesto: ${itemError.message}`);
          }

          insertedItems.push(inserted);
        }

        console.log('Budget items created successfully');
      }

      const budgetWithItems = { ...budgetData, items: insertedItems };

      // Actualizar estado local
      set(state => ({
        budgets: [budgetWithItems, ...state.budgets]
      }));

      console.log('Local state updated');

        logActivity({ action: 'Presupuesto creado', entity_type: 'budget', entity_id: budgetData.id, details: `Presupuesto ${budgetData.number}`, action_type: 'create' });

      console.log('Budget creation completed successfully');
      
      // Return the created budget with items
      return budgetWithItems;

    } catch (error: any) {
      console.error('Error adding budget:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateBudget: async (id, updates, items) => {
    try {
      const { data, error } = await supabase
        .from('budgets')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      let syncedItems: BudgetItem[] = [];

      if (items) {
        const { data: existingItems, error: fetchError } = await supabase
          .from('budget_items')
          .select('*')
          .eq('budget_id', id);

        if (fetchError) throw fetchError;

        const existingMap = new Map((existingItems || []).map(item => [item.id, item]));
        const keptIds = new Set<string>();

        for (let i = 0; i < items.length; i++) {
          const { pendingImage, previewUrl: _previewUrl, id: itemId, ...itemRest } = items[i];
          let image_url = itemRest.image_url ?? null;

          if (pendingImage) {
            const oldUrl = itemId ? existingMap.get(itemId)?.image_url : null;
            if (oldUrl) await removeBudgetItemImageFromStorage(oldUrl);
            image_url = await uploadBudgetItemImage(id, pendingImage, i);
          }

          const payload = {
            description: itemRest.description,
            quantity: itemRest.quantity,
            unit_price: itemRest.unit_price,
            tax_rate: itemRest.tax_rate,
            total: itemRest.total,
            image_url,
            budget_id: id,
          };

          if (itemId && existingMap.has(itemId)) {
            const { data: updated, error: updateError } = await supabase
              .from('budget_items')
              .update(payload)
              .eq('id', itemId)
              .select()
              .single();

            if (updateError) throw updateError;
            syncedItems.push(updated);
            keptIds.add(itemId);
          } else {
            const { data: inserted, error: insertError } = await supabase
              .from('budget_items')
              .insert([payload])
              .select()
              .single();

            if (insertError) throw insertError;
            syncedItems.push(inserted);
          }
        }

        for (const existing of existingItems || []) {
          if (!keptIds.has(existing.id)) {
            if (existing.image_url) await removeBudgetItemImageFromStorage(existing.image_url);
            const { error: deleteError } = await supabase
              .from('budget_items')
              .delete()
              .eq('id', existing.id);
            if (deleteError) throw deleteError;
          }
        }
      } else {
        const { data: existingItems } = await supabase
          .from('budget_items')
          .select('*')
          .eq('budget_id', id)
          .order('created_at', { ascending: true });
        syncedItems = existingItems || [];
      }

      const budgetWithItems = { ...data, items: syncedItems };

      set(state => ({
        budgets: state.budgets.map(budget =>
          budget.id === id ? budgetWithItems : budget
        ),
      }));

      logActivity({ action: 'Presupuesto actualizado', entity_type: 'budget', entity_id: id, details: `Presupuesto ${data.number}`, action_type: 'update' });

      return budgetWithItems;
    } catch (error: any) {
      console.error('Error updating budget:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteBudget: async (id) => {
    try {
      const { error } = await supabase
        .from('budgets')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        budgets: state.budgets.filter(budget => budget.id !== id)
      }));

      logActivity({ action: 'Presupuesto eliminado', entity_type: 'budget', entity_id: id, details: id, action_type: 'delete' });

    } catch (error: any) {
      console.error('Error deleting budget:', error);
      get().setError(error.message);
      throw error;
    }
  },

  convertBudgetToInvoice: async (budgetId) => {
    try {
      console.log('Converting budget to invoice:', budgetId);
      
      // Obtener el presupuesto completo con sus items
      const { data: budgetData, error: budgetError } = await supabase
        .from('budgets')
        .select(`
          *,
          items:budget_items(*)
        `)
        .eq('id', budgetId)
        .single();

      if (budgetError || !budgetData) {
        throw new Error('Presupuesto no encontrado');
      }

      console.log('Budget data:', budgetData);

      // Generar número de factura
      const { data: invoiceNumber, error: numberError } = await supabase
        .rpc('generate_simple_invoice_number', { p_type: budgetData.type });
      
      if (numberError) {
        console.error('Error generating invoice number:', numberError);
        throw new Error('Error generando número de factura');
      }

      // Crear la factura
      const { data: invoiceData, error: invoiceError } = await supabase
        .from('invoices')
        .insert({
          number: invoiceNumber,
          date: budgetData.date,
          entity_id: budgetData.entity_id,
          entity_type: budgetData.entity_type,
          type: budgetData.type,
          subtotal: budgetData.subtotal,
          tax: budgetData.tax,
          total: budgetData.total,
          balance: budgetData.total,
          issued_in_afip: false
        })
        .select()
        .single();

      if (invoiceError) {
        console.error('Error creating invoice:', invoiceError);
        throw new Error('Error creando la factura');
      }

      console.log('Invoice created:', invoiceData);

      // Crear los items de la factura
      if (budgetData.items && budgetData.items.length > 0) {
        const invoiceItems = budgetData.items.map(item => ({
          invoice_id: invoiceData.id,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          tax_rate: item.tax_rate,
          total: item.total
        }));

        const { error: itemsError } = await supabase
          .from('invoice_items')
          .insert(invoiceItems);

        if (itemsError) {
          console.error('Error creating invoice items:', itemsError);
          // Si falla la creación de items, eliminar la factura creada
          await supabase.from('invoices').delete().eq('id', invoiceData.id);
          throw new Error('Error creando items de la factura');
        }
      }

      // Eliminar el presupuesto y sus items
      const { error: deleteError } = await supabase
        .from('budgets')
        .delete()
        .eq('id', budgetId);

      if (deleteError) {
        console.error('Error deleting budget:', deleteError);
        // No lanzar error aquí, la factura ya se creó correctamente
        console.warn('Warning: Budget could not be deleted, but invoice was created successfully');
      }

      console.log('Conversion completed successfully');

      logActivity({ action: 'Presupuesto convertido a factura', entity_type: 'invoice', entity_id: invoiceData.id, details: `Factura ${invoiceData.number} desde presupuesto`, action_type: 'create' });

      // Refrescar datos
      await get().fetchBudgets();
      await get().fetchInvoices();

      return invoiceData.id;
    } catch (error: any) {
      console.error('Error converting budget to invoice:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Invoices
  addInvoice: async (invoice, items) => {
    try {
      // Insert invoice
      const { data: invoiceData, error: invoiceError } = await supabase
        .from('invoices')
        .insert([invoice])
        .select()
        .single();
      
      if (invoiceError) throw invoiceError;
      
      // Insert invoice items
      const itemsWithInvoiceId = items.map(item => ({
        ...item,
        invoice_id: invoiceData.id
      }));
      
      const { error: itemsError } = await supabase
        .from('invoice_items')
        .insert(itemsWithInvoiceId);
      
      if (itemsError) throw itemsError;
      
      // Update local state
      set(state => ({
        invoices: [...state.invoices, { ...invoiceData, items: itemsWithInvoiceId }]
      }));
      const entityName = invoice.entity_type === 'client' ? 'Factura' : 'Compra';
      logActivity({ action: `${entityName} creada`, entity_type: 'invoice', entity_id: invoiceData.id, details: `${invoiceData.number || invoiceData.id}`, action_type: 'create' });
    } catch (error: any) {
      console.error('Error adding invoice:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateInvoice: async (id, invoice) => {
    try {
      const { error } = await supabase
        .from('invoices')
        .update(invoice)
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        invoices: state.invoices.map(inv => 
          inv.id === id ? { ...inv, ...invoice } : inv
        )
      }));
      logActivity({ action: 'Factura/compra actualizada', entity_type: 'invoice', entity_id: id, details: id, action_type: 'update' });
    } catch (error: any) {
      console.error('Error updating invoice:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteInvoice: async (id) => {
    try {
      const { error } = await supabase
        .from('invoices')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      const inv = get().invoices.find(i => i.id === id);
      set(state => ({
        invoices: state.invoices.filter(inv => inv.id !== id)
      }));
      logActivity({ action: 'Factura/compra eliminada', entity_type: 'invoice', entity_id: id, details: inv?.number || id, action_type: 'delete' });
    } catch (error: any) {
      console.error('Error deleting invoice:', error);
      get().setError(error.message);
      throw error;
    }
  },

  voidInvoice: async (id) => {
    try {
      // Call the void_invoice function
      const { error } = await supabase.rpc('void_invoice', { p_invoice_id: id });
      
      if (error) throw error;
      
      // Update local state
      logActivity({ action: 'Factura anulada', entity_type: 'invoice', entity_id: id, details: id, action_type: 'update' });
      set(state => ({
        invoices: state.invoices.map(inv => 
          inv.id === id ? { ...inv, voided: true, voided_at: new Date().toISOString() } : inv
        ),
        // Also update payments for this invoice
        payments: state.payments.map(payment => {
          const paymentInvoice = state.invoices.find(inv => inv.id === payment.invoice_id);
          if (paymentInvoice && paymentInvoice.id === id) {
            return { ...payment, voided: true, voided_at: new Date().toISOString() };
          }
          return payment;
        })
      }));
    } catch (error: any) {
      console.error('Error voiding invoice:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Delivery Notes
  addDeliveryNote: async (deliveryNote, retryCount = 0) => {
    const maxRetries = 3;
    try {
      const { data, error } = await supabase
        .from('delivery_notes')
        .insert([deliveryNote])
        .select()
        .single();

      if (error) {
        const isDuplicateNumber = error.code === '23505' && error.message?.includes('delivery_notes_number_key');
        if (isDuplicateNumber && retryCount < maxRetries) {
          const newNumber = await getNextDeliveryNoteNumber();
          return get().addDeliveryNote({ ...deliveryNote, number: newNumber }, retryCount + 1);
        }
        throw error;
      }

      set(state => ({
        deliveryNotes: [...state.deliveryNotes, data]
      }));

      logActivity({ action: 'Remito creado', entity_type: 'delivery_note', entity_id: data.id, details: data.number || data.id, action_type: 'create' });
      await notifyEntityCreated('express_order', data, deliveryNote.client_name);
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  updateDeliveryNote: async (id, deliveryNote) => {
    try {
      console.log('Updating delivery note in Supabase:', id, deliveryNote);
      
      const { data, error } = await supabase
        .from('delivery_notes')
        .update(deliveryNote)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        console.error('Supabase error updating delivery note:', error);
        throw error;
      }
      
      console.log('Successfully updated delivery note:', data);
      
      // Update local state
      set(state => ({
        deliveryNotes: state.deliveryNotes.map(note => 
          note.id === id ? { ...note, ...data } : note
        )
      }));
      
      // Send notification
      await notifyEntityUpdated('express_order', data, deliveryNote.client_name);
      
    } catch (error: any) {
      console.error('Error updating delivery note:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteDeliveryNote: async (id) => {
    try {
      console.log('Deleting delivery note from Supabase:', id);
      
      const { error } = await supabase
        .from('delivery_notes')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Supabase error deleting delivery note:', error);
        throw error;
      }
      
      console.log('Successfully deleted delivery note:', id);
      
      // Update local state
      set(state => ({
        deliveryNotes: state.deliveryNotes.filter(note => note.id !== id)
      }));
      
    } catch (error: any) {
      console.error('Error deleting delivery note:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Service Agreements
  addServiceAgreement: async (agreement) => {
    try {
      console.log('Adding service agreement to Supabase:', agreement);
      
      const { data, error } = await supabase
        .from('service_agreements')
        .insert([agreement])
        .select()
        .single();
      
      if (error) {
        console.error('Supabase error adding service agreement:', error);
        throw error;
      }
      
      console.log('Successfully added service agreement:', data);
      
      // Update local state
      set(state => ({
        serviceAgreements: [...state.serviceAgreements, data]
      }));
      
      // Send notification
      await notifyEntityCreated('express_order', data, agreement.client_name);
      
    } catch (error: any) {
      console.error('Error adding service agreement:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateServiceAgreement: async (id, agreement) => {
    try {
      console.log('Updating service agreement in Supabase:', id, agreement);
      
      const { data, error } = await supabase
        .from('service_agreements')
        .update(agreement)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        console.error('Supabase error updating service agreement:', error);
        throw error;
      }
      
      console.log('Successfully updated service agreement:', data);
      
      // Update local state
      set(state => ({
        serviceAgreements: state.serviceAgreements.map(agr => 
          agr.id === id ? { ...agr, ...data } : agr
        )
      }));
      
      // Send notification
      await notifyEntityUpdated('express_order', data, agreement.client_name);
      
    } catch (error: any) {
      console.error('Error updating service agreement:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteServiceAgreement: async (id) => {
    try {
      console.log('Deleting service agreement from Supabase:', id);
      
      const { error } = await supabase
        .from('service_agreements')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Supabase error deleting service agreement:', error);
        throw error;
      }
      
      console.log('Successfully deleted service agreement:', id);
      
      // Update local state
      set(state => ({
        serviceAgreements: state.serviceAgreements.filter(agr => agr.id !== id)
      }));
      
    } catch (error: any) {
      console.error('Error deleting service agreement:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Service Agreement Templates
  fetchServiceAgreementTemplates: async () => {
    try {
      console.log('Fetching service agreement templates from Supabase...');

      const { data, error } = await supabase
        .from('service_agreement_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error fetching templates:', error);
        throw error;
      }

      console.log(`Successfully fetched ${data?.length || 0} templates`);
      set({ serviceAgreementTemplates: data || [] });
    } catch (error: any) {
      console.error('Error in fetchServiceAgreementTemplates:', error);
      get().setError(error.message);
      throw error;
    }
  },

  getActiveServiceAgreementTemplate: async () => {
    try {
      const { data, error } = await supabase
        .from('service_agreement_templates')
        .select('*')
        .eq('is_active', true)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data;
    } catch (error: any) {
      console.error('Error getting active template:', error);
      return null;
    }
  },

  addServiceAgreementTemplate: async (template) => {
    try {
      console.log('Adding service agreement template to Supabase:', template);

      const { data, error } = await supabase
        .from('service_agreement_templates')
        .insert([template])
        .select()
        .single();

      if (error) {
        console.error('Supabase error adding template:', error);
        throw error;
      }

      console.log('Successfully added template:', data);

      set(state => ({
        serviceAgreementTemplates: [...state.serviceAgreementTemplates, data]
      }));

      return data;
    } catch (error: any) {
      console.error('Error adding template:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateServiceAgreementTemplate: async (id, template) => {
    try {
      console.log('Updating service agreement template in Supabase:', id, template);

      const { data, error } = await supabase
        .from('service_agreement_templates')
        .update(template)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Supabase error updating template:', error);
        throw error;
      }

      console.log('Successfully updated template:', data);

      set(state => ({
        serviceAgreementTemplates: state.serviceAgreementTemplates.map(tpl =>
          tpl.id === id ? { ...tpl, ...data } : tpl
        )
      }));

      return data;
    } catch (error: any) {
      console.error('Error updating template:', error);
      get().setError(error.message);
      throw error;
    }
  },

  activateServiceAgreementTemplate: async (id) => {
    try {
      console.log('Activating template:', id);

      const { error } = await supabase.rpc('activate_service_agreement_template', {
        template_id: id
      });

      if (error) {
        console.error('Supabase error activating template:', error);
        throw error;
      }

      console.log('Successfully activated template');

      await get().fetchServiceAgreementTemplates();
    } catch (error: any) {
      console.error('Error activating template:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteServiceAgreementTemplate: async (id) => {
    try {
      console.log('Deleting service agreement template from Supabase:', id);

      const { error } = await supabase
        .from('service_agreement_templates')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Supabase error deleting template:', error);
        throw error;
      }

      console.log('Successfully deleted template:', id);

      set(state => ({
        serviceAgreementTemplates: state.serviceAgreementTemplates.filter(tpl => tpl.id !== id)
      }));

    } catch (error: any) {
      console.error('Error deleting template:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Payments
  addPayment: async (payment) => {
    try {
      const { data, error } = await supabase
        .from('payments')
        .insert([payment])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        payments: [...state.payments, data]
      }));
      const isCreditNote = (payment as any).invoice_type === 'credit_note';
      logActivity({
        action: isCreditNote ? 'Nota de crédito creada' : 'Pago registrado',
        entity_type: 'payment',
        entity_id: data.id,
        details: isCreditNote ? `NC ${(payment as any).amount}` : `Pago ${(payment as any).amount} - ${(payment as any).method || 'N/A'}`,
        action_type: 'create'
      });
    } catch (error: any) {
      console.error('Error adding payment:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updatePayment: async (id, payment) => {
    try {
      const { error } = await supabase
        .from('payments')
        .update(payment)
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        payments: state.payments.map(p => 
          p.id === id ? { ...p, ...payment } : p
        )
      }));
      logActivity({ action: 'Pago actualizado', entity_type: 'payment', entity_id: id, details: id, action_type: 'update' });
    } catch (error: any) {
      console.error('Error updating payment:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deletePayment: async (id) => {
    try {
      // Get the payment before deleting it to update invoice balance
      const { payments, invoices } = get();
      const payment = payments.find(p => p.id === id);
      
      const { error } = await supabase
        .from('payments')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      // If this payment was associated with an invoice, update the invoice balance
      if (payment && payment.invoice_id) {
        const invoice = invoices.find(inv => inv.id === payment.invoice_id);
        if (invoice) {
          // Restore the invoice balance by adding back the payment amount
          const newBalance = invoice.balance + payment.amount;
          
          // Update the invoice balance in the database
          const { error: updateError } = await supabase
            .from('invoices')
            .update({ balance: newBalance })
            .eq('id', payment.invoice_id);
          
          if (updateError) throw updateError;
          
          // Update local state
          set(state => ({
            invoices: state.invoices.map(inv => 
              inv.id === payment.invoice_id ? { ...inv, balance: newBalance } : inv
            )
          }));
        }
      }
      
      set(state => ({
        payments: state.payments.filter(p => p.id !== id)
      }));
      const isCN = payment?.invoice_type === 'credit_note';
      logActivity({ action: isCN ? 'Nota de crédito eliminada' : 'Pago eliminado', entity_type: 'payment', entity_id: id, details: id, action_type: 'delete' });
    } catch (error: any) {
      console.error('Error deleting payment:', error);
      get().setError(error.message);
      throw error;
    }
  },

  voidPayment: async (id) => {
    try {
      // Get the payment before voiding it to update invoice balance
      const { payments, invoices } = get();
      const payment = payments.find(p => p.id === id);
      
      // Call the void_payment function
      const { error } = await supabase.rpc('void_payment', { p_payment_id: id });
      
      if (error) throw error;
      
      // If this payment was associated with an invoice, update the invoice balance
      if (payment && payment.invoice_id && !payment.voided) {
        const invoice = invoices.find(inv => inv.id === payment.invoice_id);
        if (invoice) {
          // Restore the invoice balance by adding back the payment amount
          const newBalance = invoice.balance + payment.amount;
          
          // Update the invoice balance in the database
          const { error: updateError } = await supabase
            .from('invoices')
            .update({ balance: newBalance })
            .eq('id', payment.invoice_id);
          
          if (updateError) throw updateError;
          
          // Update local state
          set(state => ({
            invoices: state.invoices.map(inv => 
              inv.id === payment.invoice_id ? { ...inv, balance: newBalance } : inv
            )
          }));
        }
      }
      
      logActivity({ action: 'Pago anulado', entity_type: 'payment', entity_id: id, details: id, action_type: 'update' });
      // Update local state
      set(state => ({
        payments: state.payments.map(p => 
          p.id === id ? { ...p, voided: true, voided_at: new Date().toISOString() } : p
        )
      }));
    } catch (error: any) {
      console.error('Error voiding payment:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Productions
  addProduction: async (production) => {
    try {
      const { data, error } = await supabase
        .from('productions')
        .insert([production])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        productions: [...state.productions, data]
      }));

      // Enviar notificación de nueva producción creada
      const client = get().clients.find(c => c.id === production.client_id);
      
      // Enviar notificación a Make.com
      await notifyEntityCreated('production', data, client?.name);
      
      // Enviar notificación local a usuarios de producción
      try {
        const { NotificationService } = await import('../utils/notificationService');
        await NotificationService.notifyNewProduction(data.id, data.name, client?.name || 'Cliente');
      } catch (error) {
        console.error('Error sending local notification:', error);
      }
      logActivity({ action: 'Producción creada', entity_type: 'production', entity_id: data.id, details: data.name, action_type: 'create' });
    } catch (error: any) {
      console.error('Error adding production:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateProduction: async (id, production) => {
    try {
      // Obtener el estado anterior para detectar cambios
      const previousProduction = get().productions.find(p => p.id === id);
      const previousStatus = previousProduction?.status;

      const { error } = await supabase
        .from('productions')
        .update(production)
        .eq('id', id);
      
      if (error) throw error;
      
      const updatedProduction = { ...previousProduction, ...production };
      
      set(state => ({
        productions: state.productions.map(p => 
          p.id === id ? updatedProduction : p
        )
      }));

      // Si se revirtió el estado de finished/shipped, eliminar encuesta pendiente
      const wasFinished = previousStatus === 'finished' || previousStatus === 'shipped' || previousStatus === 'shipped/despatched';
      const isNowFinished = (updatedProduction.status === 'finished' || updatedProduction.status === 'shipped' || updatedProduction.status === 'shipped/despatched');
      if (wasFinished && !isNowFinished) {
        const { deletePendingSurveysForEntity } = await import('../utils/satisfactionSurvey');
        await deletePendingSurveysForEntity('production', id);
      }

      // Enviar notificación de actualización
      const client = get().clients.find(c => c.id === updatedProduction.client_id);
      await notifyEntityUpdated('production', updatedProduction, client?.name, previousStatus);
      logActivity({ action: 'Producción actualizada', entity_type: 'production', entity_id: id, details: updatedProduction.name, action_type: 'update' });
    } catch (error: any) {
      console.error('Error updating production:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteProduction: async (id) => {
    try {
      const prod = get().productions.find(p => p.id === id);
      const shouldArchive = prod && (prod.status === 'finished' || (prod as any).status === 'shipped' || (prod as any).status === 'despatched');

      if (shouldArchive) {
        // archive with metadata
        const { data: auth } = await supabase.auth.getUser();
        const archived_at = new Date().toISOString();
        const archived_by = auth?.user?.id || null;
        const archived_by_email = auth?.user?.email || null;
        const archived_original_status = prod?.status || null;
        const { error } = await supabase
          .from('productions')
          .update({ archived: true, archived_at, archived_by, archived_by_email, archived_original_status })
          .eq('id', id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('productions')
          .delete()
          .eq('id', id);
        if (error) throw error;
      }

      set(state => ({
        productions: state.productions.filter(p => p.id !== id)
      }));
      logActivity({ action: shouldArchive ? 'Producción archivada' : 'Producción eliminada', entity_type: 'production', entity_id: id, details: prod?.name || id, action_type: 'delete' });
    } catch (error: any) {
      console.error('Error deleting production:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Production Tasks
  addProductionTask: async (task) => {
    try {
      const { data, error } = await supabase
        .from('production_tasks')
        .insert([task])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        productionTasks: [...state.productionTasks, data]
      }));
    } catch (error: any) {
      console.error('Error adding production task:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateProductionTask: async (id, task) => {
    try {
      const { error } = await supabase
        .from('production_tasks')
        .update(task)
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        productionTasks: state.productionTasks.map(t => 
          t.id === id ? { ...t, ...task } : t
        )
      }));
    } catch (error: any) {
      console.error('Error updating production task:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteProductionTask: async (id) => {
    try {
      const { error } = await supabase
        .from('production_tasks')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        productionTasks: state.productionTasks.filter(t => t.id !== id)
      }));
    } catch (error: any) {
      console.error('Error deleting production task:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Samples
  addSample: async (sample) => {
    try {
      const { data, error } = await supabase
        .from('samples')
        .insert([sample])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        samples: [...state.samples, data]
      }));

      // Enviar notificación de nueva muestra creada
      const client = get().clients.find(c => c.id === sample.client_id);
      
      // Enviar notificación a Make.com
      await notifyEntityCreated('sample', data, client?.name);
      
      // Enviar notificación local a usuarios de producción
      try {
        const { NotificationService } = await import('../utils/notificationService');
        await NotificationService.notifyNewSample(data.id, data.name, client?.name || 'Cliente');
      } catch (error) {
        console.error('Error sending local notification:', error);
      }
      logActivity({ action: 'Muestra creada', entity_type: 'sample', entity_id: data.id, details: data.name, action_type: 'create' });
    } catch (error: any) {
      console.error('Error adding sample:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateSample: async (id, sample) => {
    try {
      // Obtener el estado anterior para detectar cambios
      const previousSample = get().samples.find(s => s.id === id);
      const previousStatus = previousSample?.status;

      const { error } = await supabase
        .from('samples')
        .update(sample)
        .eq('id', id);
      
      if (error) throw error;
      
      const updatedSample = { ...previousSample, ...sample };
      
      set(state => ({
        samples: state.samples.map(s => 
          s.id === id ? updatedSample : s
        )
      }));

      // Si se revirtió el estado de finished/shipped, eliminar encuesta pendiente
      const wasFinished = previousStatus === 'finished' || previousStatus === 'shipped' || previousStatus === 'shipped/despatched';
      const isNowFinished = (updatedSample.status === 'finished' || updatedSample.status === 'shipped' || updatedSample.status === 'shipped/despatched');
      if (wasFinished && !isNowFinished) {
        const { deletePendingSurveysForEntity } = await import('../utils/satisfactionSurvey');
        await deletePendingSurveysForEntity('sample', id);
      }

      // Enviar notificación de actualización
      const client = get().clients.find(c => c.id === updatedSample.client_id);
      await notifyEntityUpdated('sample', updatedSample, client?.name, previousStatus);
      logActivity({ action: 'Muestra actualizada', entity_type: 'sample', entity_id: id, details: updatedSample.name, action_type: 'update' });
    } catch (error: any) {
      console.error('Error updating sample:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteSample: async (id) => {
    try {
      const samp = get().samples.find(s => s.id === id);
      const shouldArchive = samp && (samp.status === 'finished' || samp.status === 'shipped');

      if (shouldArchive) {
        const { data: auth } = await supabase.auth.getUser();
        const archived_at = new Date().toISOString();
        const archived_by = auth?.user?.id || null;
        const archived_by_email = auth?.user?.email || null;
        const archived_original_status = samp?.status || null;
        const { error } = await supabase
          .from('samples')
          .update({ archived: true, archived_at, archived_by, archived_by_email, archived_original_status })
          .eq('id', id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('samples')
          .delete()
          .eq('id', id);
        if (error) throw error;
      }

      set(state => ({
        samples: state.samples.filter(s => s.id !== id)
      }));
      logActivity({ action: shouldArchive ? 'Muestra archivada' : 'Muestra eliminada', entity_type: 'sample', entity_id: id, details: samp?.name || id, action_type: 'delete' });
    } catch (error: any) {
      console.error('Error deleting sample:', error);
      get().setError(error.message);
      throw error;
    }
  },

  restoreProduction: async (id) => {
    try {
      const { error } = await supabase
        .from('productions')
        .update({ archived: false, archived_at: null })
        .eq('id', id);
      if (error) throw error;
      set(state => ({
        productions: state.productions.filter(p => p.id !== id)
      }));
    } catch (error: any) {
      console.error('Error restoring production:', error);
      get().setError(error.message);
      throw error;
    }
  },

  restoreSample: async (id) => {
    try {
      const { error } = await supabase
        .from('samples')
        .update({ archived: false, archived_at: null })
        .eq('id', id);
      if (error) throw error;
      set(state => ({
        samples: state.samples.filter(s => s.id !== id)
      }));
    } catch (error: any) {
      console.error('Error restoring sample:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Sample Tasks
  addSampleTask: async (task) => {
    try {
      const { data, error } = await supabase
        .from('sample_tasks')
        .insert([task])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        sampleTasks: [...state.sampleTasks, data]
      }));
    } catch (error: any) {
      console.error('Error adding sample task:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateSampleTask: async (id, task) => {
    try {
      const { error } = await supabase
        .from('sample_tasks')
        .update(task)
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        sampleTasks: state.sampleTasks.map(t => 
          t.id === id ? { ...t, ...task } : t
        )
      }));
    } catch (error: any) {
      console.error('Error updating sample task:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteSampleTask: async (id) => {
    try {
      const { error } = await supabase
        .from('sample_tasks')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        sampleTasks: state.sampleTasks.filter(t => t.id !== id)
      }));
    } catch (error: any) {
      console.error('Error deleting sample task:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Express
addExpress: async (order, items) => {
  try {
    /* 1) Insertar la orden y pedir la fila completa para obtener el id real */
    const { data: savedOrder, error: orderErr } = await supabase
      .from('express')
      .insert([order])
      .select()          // ← obliga a devolver la fila insertada
      .single();         // ← la fila viene como objeto, no array

    if (orderErr) throw orderErr;

    /* 2) Insertar los ítems (si hay) ya con el id real */
    if (items && items.length) {
      const itemsWithId = items.map(itm => ({
        ...itm,
        express_id: savedOrder.id,
      }));

      const { error: itemsErr } = await supabase
        .from('express_items')
        .insert(itemsWithId);

      if (itemsErr) throw itemsErr;
    }

    /* 3) Como todo salió bien, actualizamos el estado global */
    set(state => ({
      express: [...state.express, savedOrder],
    }));

    /* 4) Enviar notificación de nueva orden express creada */
    const client = get().clients.find(c => c.id === order.client_id);
    
    // Enviar notificación a Make.com
    await notifyEntityCreated('express_order', savedOrder, client?.name);
    
    // Enviar notificación local a usuarios de producción
    try {
      const { NotificationService } = await import('../utils/notificationService');
      await NotificationService.notifyNewExpress(savedOrder.id, savedOrder.name, client?.name || 'Cliente');
    } catch (error) {
      console.error('Error sending local notification:', error);
    }

    logActivity({ action: 'Pedido express creado', entity_type: 'express', entity_id: savedOrder.id, details: savedOrder.name, action_type: 'create' });

    /* 5) Devolver el id real a la modal */
    return savedOrder.id;
  } catch (error) {
    console.error('Error adding express order:', error);
    get().setError(error.message);
    throw error; // la modal mostrará ErrorModal y no limpiará el formulario
  }
},

  updateExpress: async (id, express) => {
    try {
      // Extract items from the express object
      const { items, ...expressData } = express;
      
      // Update express order
      const { error: expressError } = await supabase
        .from('express')
        .update(expressData)
        .eq('id', id);
      
      if (expressError) throw expressError;
      
      // Update express items if provided
      if (items && items.length > 0) {
        // First, delete existing items
        const { error: deleteError } = await supabase
          .from('express_items')
          .delete()
          .eq('express_id', id);
        
        if (deleteError) throw deleteError;
        
        // Then, insert new items
        const itemsWithExpressId = items.map(item => ({
          ...item,
          express_id: id
        }));
        
        const { error: itemsError } = await supabase
          .from('express_items')
          .insert(itemsWithExpressId);
        
        if (itemsError) throw itemsError;
        
        // Update local state
        set(state => ({
          express: state.express.map(e => 
            e.id === id ? { ...e, ...expressData } : e
          ),
          expressItems: [
            ...state.expressItems.filter(item => item.express_id !== id),
            ...itemsWithExpressId.map(item => ({
              ...item,
              id: uuidv4(), // Temporary ID until we fetch the real ones
              created_at: new Date().toISOString()
            }))
          ]
        }));
      } else {
        // Update local state without changing items
        set(state => ({
            express: state.express.map(e => 
              e.id === id ? { ...e, ...expressData } : e
            )
          }));
      }
      logActivity({ action: 'Pedido express actualizado', entity_type: 'express', entity_id: id, details: id, action_type: 'update' });
    } catch (error: any) {
      console.error('Error updating express order:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteExpress: async (id) => {
    try {
      const { error } = await supabase
        .from('express')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        express: state.express.filter(e => e.id !== id),
        expressItems: state.expressItems.filter(item => item.express_id !== id)
      }));
      logActivity({ action: 'Pedido express eliminado', entity_type: 'express', entity_id: id, details: id, action_type: 'delete' });
    } catch (error: any) {
      console.error('Error deleting express order:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Express Stock
  addExpressStock: async (stock) => {
    try {
      const { data, error } = await supabase
        .from('express_stock')
        .insert([stock])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        expressStock: [...state.expressStock, data]
      }));
    } catch (error: any) {
      console.error('Error adding express stock:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateExpressStock: async (id, stock) => {
    try {
      const { error } = await supabase
        .from('express_stock')
        .update(stock)
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        expressStock: state.expressStock.map(s => 
          s.id === id ? { ...s, ...stock } : s
        )
      }));
    } catch (error: any) {
      console.error('Error updating express stock:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteExpressStock: async (id) => {
    try {
      const { error } = await supabase
        .from('express_stock')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        expressStock: state.expressStock.filter(s => s.id !== id)
      }));
    } catch (error: any) {
      console.error('Error deleting express stock:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Express Stock Movements
  addExpressStockMovement: async (movement) => {
    try {
      const { data, error } = await supabase
        .from('express_stock_movements')
        .insert([movement])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        expressStockMovements: [...state.expressStockMovements, data]
      }));
    } catch (error: any) {
      console.error('Error adding express stock movement:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Express Samples
  addExpressSample: async (sample, items) => {
    try {
      // Insert express sample
      const { data: sampleData, error: sampleError } = await supabase
        .from('express_samples')
        .insert([sample])
        .select()
        .single();
      
      if (sampleError) throw sampleError;
      
      // Insert express sample items
      if (items && items.length > 0) {
        const itemsWithSampleId = items.map(item => ({
          ...item,
          express_sample_id: sampleData.id
        }));
        
        const { error: itemsError } = await supabase
          .from('express_sample_items')
          .insert(itemsWithSampleId);
        
        if (itemsError) throw itemsError;
        
        // Update local state with items
        const newItems = itemsWithSampleId.map(item => ({
          ...item,
          id: uuidv4(), // Temporary ID until we fetch the real ones
          created_at: new Date().toISOString()
        }));
        
        set(state => ({
          expressSamples: [
  ...state.expressSamples,
  { ...sampleData, file_names: sample.file_names }                // ← NUEVO
],
          expressSampleItems: [...state.expressSampleItems, ...newItems]
        }));
      } else {
        // Update local state without items
        set(state => ({
          expressSamples: [...state.expressSamples, sampleData]
        }));
      }

      // Enviar notificación de nueva muestra express creada
      const client = get().clients.find(c => c.id === sample.client_id);
      
      // Enviar notificación a Make.com
      await notifyEntityCreated('express_sample', sampleData, client?.name);
      
      // Enviar notificación local a usuarios de producción
      try {
        const { NotificationService } = await import('../utils/notificationService');
        await NotificationService.notifyNewExpressSample(sampleData.id, sampleData.name, client?.name || 'Cliente');
      } catch (error) {
        console.error('Error sending local notification:', error);
      }
      
      logActivity({ action: 'Muestra express creada', entity_type: 'express_sample', entity_id: sampleData.id, details: sampleData.name, action_type: 'create' });
      return sampleData.id;
    } catch (error: any) {
      console.error('Error adding express sample:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateExpressSample: async (id, sample) => {
    try {
      // Extract items from the sample object
      const { items, ...sampleData } = sample;
      
      // Update express sample
      const { error: sampleError } = await supabase
        .from('express_samples')
        .update(sampleData)
        .eq('id', id);
      
      if (sampleError) throw sampleError;
      
      // Update express sample items if provided
      if (items && items.length > 0) {
        // First, delete existing items
        const { error: deleteError } = await supabase
          .from('express_sample_items')
          .delete()
          .eq('express_sample_id', id);
        
        if (deleteError) throw deleteError;
        
        // Then, insert new items
        const itemsWithSampleId = items.map(item => ({
          ...item,
          express_sample_id: id
        }));
        
        const { error: itemsError } = await supabase
          .from('express_sample_items')
          .insert(itemsWithSampleId);
        
        if (itemsError) throw itemsError;
        
        // Update local state
        set(state => ({
          expressSamples: state.expressSamples.map(s => 
            s.id === id ? { ...s, ...sampleData, file_names: sample.file_names } : s
          ),
          expressSampleItems: [
            ...state.expressSampleItems.filter(item => item.express_sample_id !== id),
            ...itemsWithSampleId.map(item => ({
              ...item,
              id: uuidv4(), // Temporary ID until we fetch the real ones
              created_at: new Date().toISOString()
            }))
          ]
        }));
      } else {
        // Update local state without changing items
        set(state => ({
          expressSamples: state.expressSamples.map(s => 
            s.id === id ? { ...s, ...sampleData, file_names: sample.file_names } : s
          )
        }));
      }
      logActivity({ action: 'Muestra express actualizada', entity_type: 'express_sample', entity_id: id, details: sampleData.name || id, action_type: 'update' });
    } catch (error: any) {
      console.error('Error updating express sample:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteExpressSample: async (id) => {
    try {
      const { error } = await supabase
        .from('express_samples')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        expressSamples: state.expressSamples.filter(s => s.id !== id),
        expressSampleItems: state.expressSampleItems.filter(item => item.express_sample_id !== id)
      }));
      logActivity({ action: 'Muestra express eliminada', entity_type: 'express_sample', entity_id: id, details: id, action_type: 'delete' });
    } catch (error: any) {
      console.error('Error deleting express sample:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Todo Items
  addTodoItem: async (item) => {
    try {
      const { data, error } = await supabase
        .from('todo_items')
        .insert([item])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        todoItems: [...state.todoItems, data]
      }));
      
      // Update order in localStorage
      const savedOrder = localStorage.getItem('todo_items_order');
      if (savedOrder) {
        try {
          const orderArray = JSON.parse(savedOrder);
          // Add new task ID at the beginning (newest first)
          const newOrder = [data.id, ...orderArray];
          localStorage.setItem('todo_items_order', JSON.stringify(newOrder));
        } catch (e) {
          console.error('Error updating todo order:', e);
        }
      } else {
        // Initialize order with just this task
        localStorage.setItem('todo_items_order', JSON.stringify([data.id]));
      }
      
      return data;
    } catch (error: any) {
      console.error('Error adding todo item:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateTodoItem: async (id, item) => {
    try {
      const { error } = await supabase
        .from('todo_items')
        .update(item)
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        todoItems: state.todoItems.map(t => 
          t.id === id ? { ...t, ...item } : t
        )
      }));
    } catch (error: any) {
      console.error('Error updating todo item:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteTodoItem: async (id) => {
    try {
      const { error } = await supabase
        .from('todo_items')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        todoItems: state.todoItems.filter(t => t.id !== id)
      }));
    } catch (error: any) {
      console.error('Error deleting todo item:', error);
      get().setError(error.message);
      throw error;
    }
  },

  toggleTodoItemCompletion: async (id, completed) => {
    try {
      const { error } = await supabase
        .from('todo_items')
        .update({ completed, status: completed ? 'Hecho' : 'Por hacer' })
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        todoItems: state.todoItems.map(t => 
          t.id === id ? { ...t, completed, status: completed ? 'Hecho' : 'Por hacer' } : t
        )
      }));
    } catch (error: any) {
      console.error('Error toggling todo item completion:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Task boards CRUD
  addTaskBoard: async (board: Omit<TaskBoard, 'id' | 'created_at' | 'updated_at' | 'is_archived'>) => {
    try {
      const payload = {
        name: board.name,
        description: board.description ?? null,
        status_columns: board.status_columns ?? null,
      };

      const { data, error } = await supabase
        .from('task_boards')
        .insert([payload])
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        taskBoards: [...state.taskBoards, data],
      }));

      return data;
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  updateTaskBoard: async (id: string, updates: Partial<TaskBoard>) => {
    try {
      const { data, error } = await supabase
        .from('task_boards')
        .update({
          name: updates.name,
          description: updates.description ?? null,
          is_archived: updates.is_archived,
          status_columns: updates.status_columns ?? null,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        taskBoards: state.taskBoards.map(b => (b.id === id ? data : b)),
      }));

      return data;
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  deleteTaskBoard: async (id: string) => {
    try {
      const { error } = await supabase
        .from('task_boards')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        taskBoards: state.taskBoards.filter(b => b.id !== id),
        taskBoardTasks: state.taskBoardTasks.filter(t => t.board_id !== id),
      }));
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  addTaskBoardTask: async (task: Omit<TaskBoardTask, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const payload: Record<string, unknown> = {
        board_id: task.board_id ?? null,
        project_id: task.project_id ?? null,
        title: task.title,
        description: task.description ?? null,
        status: task.status,
        due_date: task.due_date ?? null,
        start_date: task.start_date ?? null,
        priority: task.priority ?? 'medium',
        responsible_id: task.responsible_id ?? null,
        category_id: task.category_id ?? null,
        assignee: task.assignee ?? null,
        tags: task.tags ?? [],
        checklist: task.checklist ?? [],
        comments: task.comments ?? [],
        attachments: task.attachments ?? [],
        activity: task.activity ?? [],
        is_archived: task.is_archived ?? false,
        completed: task.completed ?? false,
        position: task.position ?? 0,
        number_value: task.number_value ?? null,
      };

      const { data, error } = await supabase
        .from('task_board_tasks')
        .insert([payload])
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        taskBoardTasks: [...state.taskBoardTasks, data],
      }));

      return data;
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  updateTaskBoardTask: async (id: string, updates: Partial<TaskBoardTask>) => {
    try {
      const payload: Record<string, unknown> = {};
      const fields = [
        'title', 'description', 'status', 'due_date', 'start_date', 'priority',
        'responsible_id', 'category_id', 'project_id', 'assignee', 'tags',
        'checklist', 'comments', 'attachments', 'activity', 'is_archived',
        'completed', 'position', 'board_id', 'number_value',
      ] as const;
      for (const key of fields) {
        if (key in updates) payload[key] = updates[key] ?? null;
      }

      const { data, error } = await supabase
        .from('task_board_tasks')
        .update(payload)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        taskBoardTasks: state.taskBoardTasks.map(t => (t.id === id ? data : t)),
      }));

      return data;
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  deleteTaskBoardTask: async (id: string) => {
    try {
      const { error } = await supabase
        .from('task_board_tasks')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        taskBoardTasks: state.taskBoardTasks.filter(t => t.id !== id),
      }));
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  // Responsables, categorías y proyectos
  addTaskResponsible: async (responsible: Omit<TaskResponsible, 'id' | 'created_at' | 'updated_at' | 'is_active'>) => {
    try {
      const payload = {
        name: responsible.name,
        email: responsible.email ?? null,
      };

      const { data, error } = await supabase
        .from('task_responsibles')
        .insert([payload])
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        taskResponsibles: [...state.taskResponsibles, data],
      }));

      return data;
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  updateTaskResponsible: async (id: string, updates: Partial<TaskResponsible>) => {
    try {
      const { data, error } = await supabase
        .from('task_responsibles')
        .update({
          name: updates.name,
          email: updates.email ?? null,
          is_active: updates.is_active,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        taskResponsibles: state.taskResponsibles.map(r => (r.id === id ? data : r)),
      }));

      return data;
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  deleteTaskResponsible: async (id: string) => {
    try {
      const { error } = await supabase
        .from('task_responsibles')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        taskResponsibles: state.taskResponsibles.filter(r => r.id !== id),
      }));
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  addTaskCategory: async (category: Omit<TaskCategory, 'id' | 'created_at' | 'updated_at' | 'is_active'>) => {
    try {
      const payload = {
        name: category.name,
        color: category.color ?? null,
      };

      const { data, error } = await supabase
        .from('task_categories')
        .insert([payload])
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        taskCategories: [...state.taskCategories, data],
      }));

      return data;
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  updateTaskCategory: async (id: string, updates: Partial<TaskCategory>) => {
    try {
      const { data, error } = await supabase
        .from('task_categories')
        .update({
          name: updates.name,
          color: updates.color ?? null,
          is_active: updates.is_active,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        taskCategories: state.taskCategories.map(c => (c.id === id ? data : c)),
      }));

      return data;
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  deleteTaskCategory: async (id: string) => {
    try {
      const { error } = await supabase
        .from('task_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        taskCategories: state.taskCategories.filter(c => c.id !== id),
      }));
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  addTaskProject: async (project: Omit<TaskProject, 'id' | 'created_at' | 'updated_at' | 'is_active'>) => {
    try {
      const maxOrder = get().taskProjects.reduce((max, item) => Math.max(max, item.sort_order ?? 0), 0);
      const payload = {
        name: project.name,
        description: project.description ?? null,
        color: project.color ?? '#6366f1',
        status: project.status ?? 'active',
        status_columns: project.status_columns ?? null,
        sort_order: maxOrder + 1000,
        is_active: true,
      };

      const { data, error } = await supabase
        .from('task_projects')
        .insert([payload])
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        taskProjects: sortTaskProjects([...state.taskProjects, data]),
      }));

      return data;
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  updateTaskProject: async (id: string, updates: Partial<TaskProject>) => {
    try {
      const payload: Record<string, unknown> = {};
      if ('name' in updates) payload.name = updates.name;
      if ('description' in updates) payload.description = updates.description ?? null;
      if ('is_active' in updates) payload.is_active = updates.is_active;
      if ('color' in updates) payload.color = updates.color ?? null;
      if ('status' in updates) payload.status = updates.status ?? null;
      if ('status_columns' in updates) payload.status_columns = updates.status_columns ?? null;
      if ('column_colors' in updates) payload.column_colors = updates.column_colors ?? null;
      if ('column_aggregations' in updates) payload.column_aggregations = updates.column_aggregations ?? {};
      if ('sticky_notes' in updates) payload.sticky_notes = updates.sticky_notes ?? [];

      const { data, error } = await supabase
        .from('task_projects')
        .update(payload)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        taskProjects: sortTaskProjects(
          state.taskProjects.map(p => (p.id === id ? data : p))
        ),
      }));

      return data;
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  reorderTaskProjects: async (orderedIds: string[]) => {
    try {
      const orderMap = new Map(orderedIds.map((projectId, index) => [projectId, (index + 1) * 1000]));

      set(state => ({
        taskProjects: sortTaskProjects(
          state.taskProjects.map(project => (
            orderMap.has(project.id)
              ? { ...project, sort_order: orderMap.get(project.id) }
              : project
          ))
        ),
      }));

      const results = await Promise.all(
        orderedIds.map((projectId, index) =>
          supabase
            .from('task_projects')
            .update({ sort_order: (index + 1) * 1000 })
            .eq('id', projectId)
        )
      );

      const failed = results.find(result => result.error);
      if (failed?.error) throw failed.error;
    } catch (error: any) {
      console.error('Error reordering task projects:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteTaskProject: async (id: string) => {
    try {
      const { error: tasksError } = await supabase
        .from('task_board_tasks')
        .delete()
        .eq('project_id', id);

      if (tasksError) throw tasksError;

      const { error } = await supabase
        .from('task_projects')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        taskProjects: state.taskProjects.filter(p => p.id !== id),
        taskBoardTasks: state.taskBoardTasks.filter(t => t.project_id !== id),
      }));
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  // Production Todos
  addProductionTodo: async (item) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('production_todos')
        .insert([{
          ...item,
          user_id: user.id
        }])
        .select(`
          *,
          suppliers:supplier_id (
            name
          )
        `)
        .single();
      
      if (error) throw error;
      
      const transformedData = {
        ...data,
        supplier_name: data.suppliers?.name || null
      };
      
      set(state => ({
        productionTodos: [transformedData, ...state.productionTodos]
      }));
    } catch (error: any) {
      console.error('Error adding production todo:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateProductionTodo: async (id, item) => {
    try {
      const { error } = await supabase
        .from('production_todos')
        .update(item)
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        productionTodos: state.productionTodos.map(t => 
          t.id === id ? { ...t, ...item } : t
        )
      }));
    } catch (error: any) {
      console.error('Error updating production todo:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteProductionTodo: async (id) => {
    try {
      const { error } = await supabase
        .from('production_todos')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        productionTodos: state.productionTodos.filter(t => t.id !== id)
      }));
    } catch (error: any) {
      console.error('Error deleting production todo:', error);
      get().setError(error.message);
      throw error;
    }
  },

  toggleProductionTodoCompletion: async (id, completed) => {
    try {
      const { error } = await supabase
        .from('production_todos')
        .update({ completed })
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        productionTodos: state.productionTodos.map(t => 
          t.id === id ? { ...t, completed } : t
        )
      }));
    } catch (error: any) {
      console.error('Error toggling production todo completion:', error);
      get().setError(error.message);
      throw error;
    }
  },



  // Expenses
  addExpense: async (expense) => {
    try {
      const { data, error } = await supabase
        .from('expenses')
        .insert([expense])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        expenses: [...state.expenses, data]
      }));
      logActivity({ action: 'Gasto registrado', entity_type: 'expense', entity_id: data.id, details: `${(expense as any).description || 'Gasto'} - $${(expense as any).amount}`, action_type: 'create' });
    } catch (error: any) {
      console.error('Error adding expense:', error);
      get().setError(error.message);
      throw error;
    }
  },

  updateExpense: async (id, expense) => {
    try {
      const { error } = await supabase
        .from('expenses')
        .update(expense)
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        expenses: state.expenses.map(e => 
          e.id === id ? { ...e, ...expense } : e
        )
      }));
      logActivity({ action: 'Gasto actualizado', entity_type: 'expense', entity_id: id, details: (expense as any).description || id, action_type: 'update' });
    } catch (error: any) {
      console.error('Error updating expense:', error);
      get().setError(error.message);
      throw error;
    }
  },

  deleteExpense: async (id) => {
    try {
      const { error } = await supabase
        .from('expenses')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      const exp = get().expenses.find(e => e.id === id);
      set(state => ({
        expenses: state.expenses.filter(e => e.id !== id)
      }));
      logActivity({ action: 'Gasto eliminado', entity_type: 'expense', entity_id: id, details: (exp as any)?.description || id, action_type: 'delete' });
    } catch (error: any) {
      console.error('Error deleting expense:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Saved Addresses
  addSavedAddress: async (address) => {
    try {
      const { data, error } = await supabase.from('saved_addresses').insert([address]).select().single();
      if (error) throw error;
      set(state => ({ savedAddresses: [...state.savedAddresses, data] }));
      return data;
    } catch (error: any) {
      console.error('Error adding saved address:', error);
      get().setError(error.message);
      throw error;
    }
  },
  updateSavedAddress: async (id, address) => {
    try {
      const { error } = await supabase.from('saved_addresses').update(address).eq('id', id);
      if (error) throw error;
      set(state => ({
        savedAddresses: state.savedAddresses.map(a => a.id === id ? { ...a, ...address } : a)
      }));
    } catch (error: any) {
      console.error('Error updating saved address:', error);
      get().setError(error.message);
      throw error;
    }
  },
  deleteSavedAddress: async (id) => {
    try {
      const { error } = await supabase.from('saved_addresses').delete().eq('id', id);
      if (error) throw error;
      set(state => ({ savedAddresses: state.savedAddresses.filter(a => a.id !== id) }));
    } catch (error: any) {
      console.error('Error deleting saved address:', error);
      get().setError(error.message);
      throw error;
    }
  },

  // Utility functions
  calculateEntityBalance: (entityId, entityType) => {
    const { invoices, payments } = get();
    let totalInvoices = 0;
    let totalPayments = 0;
    let totalCreditNotes = 0;
    
    // Get all invoices for this entity
    const entityInvoices = invoices.filter(
      inv => inv.entity_id === entityId && 
             inv.entity_type === entityType &&
             !inv.voided
    );
    
    // Calculate total invoice amount (with currency conversion)
    totalInvoices = entityInvoices.reduce((sum, inv) => {
      let amount = inv.total || 0;
      // Convert USD to ARS if needed
      if (inv.currency === 'USD' && inv.exchange_rate) {
        amount = amount * inv.exchange_rate;
      }
      return sum + amount;
    }, 0);
    
    // Get all payments for this entity's invoices
    let entityPayments = payments.filter(payment => {
      if (payment.invoice_id) {
        // Payment associated with an invoice
        const invoice = invoices.find(inv => inv.id === payment.invoice_id);
        return invoice && 
               invoice.entity_id === entityId && 
               invoice.entity_type === entityType &&
               !payment.voided;
      } else {
        // Account payment - check reference
        return payment.reference === `${entityType}:${entityId}` && !payment.voided;
      }
    });
    
    // Get all credit notes for this entity
    const entityCreditNotes = payments.filter(payment => {
      if (payment.invoice_id && payment.invoice_type === 'credit_note') {
        // Credit note associated with an invoice
        const invoice = invoices.find(inv => inv.id === payment.invoice_id);
        return invoice && 
               invoice.entity_id === entityId && 
               invoice.entity_type === entityType &&
               !payment.voided;
      }
      return false;
    });
    
    // Calculate totals (with currency conversion)
    totalPayments = entityPayments
      .filter(p => !p.invoice_type || p.invoice_type !== 'credit_note')
      .reduce((sum, payment) => {
        let amount = payment.amount || 0;
        // Convert USD to ARS if needed
        if (payment.currency === 'USD' && payment.exchange_rate) {
          amount = amount * payment.exchange_rate;
        }
        return sum + amount;
      }, 0);
    
    totalCreditNotes = entityCreditNotes.reduce((sum, creditNote) => {
      let amount = creditNote.amount || 0;
      // Convert USD to ARS if needed
      if (creditNote.currency === 'USD' && creditNote.exchange_rate) {
        amount = amount * creditNote.exchange_rate;
      }
      return sum + amount;
    }, 0);
    
    // Calculate balance
    const balance = totalInvoices - totalPayments - totalCreditNotes;
    
    
    return balance;
  },

  getEntityBalanceByCurrency: (entityId, entityType) => {
    const { invoices, payments } = get();
    const invs = invoices.filter(inv => inv.entity_id === entityId && inv.entity_type === entityType && !inv.voided);
    let ars = 0;
    let usd = 0;
    invs.forEach(inv => {
      const curr = (inv as any).currency === 'USD' ? 'USD' : 'ARS';
      if (curr === 'ARS') ars += inv.total ?? 0;
      else usd += inv.total ?? 0;
    });
    const pms = payments.filter(pay => {
      if (pay.invoice_id) {
        const inv = invoices.find(i => i.id === pay.invoice_id);
        return inv && inv.entity_id === entityId && inv.entity_type === entityType && !pay.voided;
      }
      return pay.reference === `${entityType}:${entityId}` && !pay.voided;
    });
    pms.forEach(pay => {
      const inv = pay.invoice_id ? invoices.find(i => i.id === pay.invoice_id) : null;
      if (inv) {
        const invoiceCurr = (inv as any).currency === 'USD' ? 'USD' : 'ARS';
        const payCurr = (pay as any).currency === 'USD' ? 'USD' : 'ARS';
        const rate = (pay as any).exchange_rate as number | undefined;
        let amountInInvoiceCurrency = pay.amount ?? 0;
        if (invoiceCurr !== payCurr && rate && rate > 0) {
          if (invoiceCurr === 'USD' && payCurr === 'ARS') amountInInvoiceCurrency = (pay.amount ?? 0) / rate;
          else if (invoiceCurr === 'ARS' && payCurr === 'USD') amountInInvoiceCurrency = (pay.amount ?? 0) * rate;
        }
        if (invoiceCurr === 'ARS') ars -= amountInInvoiceCurrency;
        else usd -= amountInInvoiceCurrency;
      } else {
        const curr = (pay as any).currency === 'USD' ? 'USD' : 'ARS';
        if (curr === 'ARS') ars -= pay.amount ?? 0;
        else usd -= pay.amount ?? 0;
      }
    });
    return { ARS: ars, USD: usd };
  },

  getEntityBillingByCurrency: (entityId, entityType) => {
    const { invoices } = get();
    const invs = invoices.filter(
      inv => inv.entity_id === entityId && inv.entity_type === entityType && !inv.voided
    );
    let ars = 0;
    let usd = 0;
    invs.forEach(inv => {
      const curr = (inv as { currency?: string }).currency === 'USD' ? 'USD' : 'ARS';
      if (curr === 'ARS') ars += inv.total ?? 0;
      else usd += inv.total ?? 0;
    });
    return { ARS: ars, USD: usd };
  },

  // CRM Functions
  fetchCrmLeads: async () => {
    try {
      // Obtener usuario actual del estado
      const currentUser = get().currentUser;
      const allUsers = get().users;
      if (!currentUser) {
        set({ crmLeads: [] });
        return;
      }

      // Construir la consulta base
      let query = supabase
        .from('crm_leads')
        .select(`
          *,
          creator:safe_users!crm_leads_vendedor_id_fkey(email)
        `)
        .order('created_at', { ascending: false });

      // Aplicar filtro por rol si es necesario
      // Solo los administradores ven todos los leads, los vendedores solo ven los suyos
      const isAdmin = currentUser.role === 'admin' || currentUser.role === 'Administrador';
      const shouldFilter = !isAdmin;
      
      if (shouldFilter) {
        query = query.eq('vendedor_id', currentUser.id);
      }

      const { data, error } = await query;

      if (error) {
        console.error('[STORE] Error fetching CRM leads:', error);
        set({ crmLeads: [] });
        return;
      }
      
      
      set({ crmLeads: data || [] });
    } catch (error: any) {
      console.error('[STORE] Error in fetchCrmLeads:', error);
      set({ crmLeads: [] });
    }
  },

  addCrmLead: async (lead: Omit<CrmLead, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('crm_leads')
        .insert([lead])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        crmLeads: [data, ...state.crmLeads]
      }));
      
      return data;
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  updateCrmLead: async (id: string, updates: Partial<CrmLead>) => {
    try {
      const { data, error } = await supabase
        .from('crm_leads')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        crmLeads: state.crmLeads.map(lead => 
          lead.id === id ? data : lead
        )
      }));
      
      return data;
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  deleteCrmLead: async (id: string) => {
    try {
      const { error } = await supabase
        .from('crm_leads')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        crmLeads: state.crmLeads.filter(lead => lead.id !== id)
      }));
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  fetchCrmReminders: async () => {
    try {
      // Obtener usuario actual del estado
      const currentUser = get().currentUser;
      if (!currentUser) {
        set({ crmReminders: [] });
        return;
      }

      // Construir la consulta base
      let query = supabase
        .from('crm_reminders')
        .select('*')
        .order('fecha_recordatorio', { ascending: true });

      // Aplicar filtro por rol si es necesario
      // Solo los administradores ven todos los recordatorios, los vendedores solo ven los suyos
      const isAdmin = currentUser.role === 'admin' || currentUser.role === 'Administrador';
      const shouldFilter = !isAdmin;
      
      if (shouldFilter) {
        query = query.eq('vendedor_id', currentUser.id);
      }

      const { data, error } = await query;
      
      if (error) {
        // Si es error de autenticación, limpiar el estado y no mostrar error
        if (error.message?.includes('permission denied') || error.message?.includes('Invalid Refresh Token')) {
          console.log('[STORE] Authentication error in fetchCrmReminders, clearing reminders data');
          set({ crmReminders: [] });
          return;
        }
        throw error;
      }
      
      set({ crmReminders: data || [] });
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  addCrmReminder: async (reminder: Omit<CrmReminder, 'id' | 'created_at'>) => {
    try {
      const { data, error } = await supabase
        .from('crm_reminders')
        .insert([reminder])
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        crmReminders: [...state.crmReminders, data]
      }));
      
      return data;
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  updateCrmReminder: async (id: string, updates: Partial<CrmReminder>) => {
    try {
      const { data, error } = await supabase
        .from('crm_reminders')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      set(state => ({
        crmReminders: state.crmReminders.map(reminder => 
          reminder.id === id ? data : reminder
        )
      }));
      
      return data;
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  },

  deleteCrmReminder: async (id: string) => {
    try {
      const { error } = await supabase
        .from('crm_reminders')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        crmReminders: state.crmReminders.filter(reminder => reminder.id !== id)
      }));
    } catch (error: any) {
      get().setError(error.message);
      throw error;
    }
  }
}));